import React, {useEffect, useRef, useState} from "react";
import {Willow, Gantt, Toolbar, defaultToolbarButtons, ContextMenu, defaultMenuOptions} from "wx-react-gantt";
import {editorShape} from "./EditorShape";
import {taskTypes} from "./TaskTypes";
import {translateToolbarButtons} from "./ToolBarItems";
import TaskTemplateComponent from "./TaskTemplateComponent";
import {columns} from "./columns";
import {bigScales} from "./bigScales";
import {dayStyle} from "./dayStaly";
import Slider from './ui/Slider.js';
import {FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight} from 'react-icons/fa';
import {translateMenuItems} from "./ContextMenuItems";
import Api from "../../../Api/api";
import {toast} from "react-toastify";
import ConsultantSelectionModal from "../../../components/modal/ConsultantSelectionModal";
import { createRoot } from 'react-dom/client';
import {
    calculateWorkingDays,
    calculateEndDateFromWorkingDays,
    isWorkingDay,
    getWorkWeekConfig,
    setWorkWeekConfig,
    getNonWorkingDayClass,
    DAY_NAMES
} from "../utils/workingDays";
/**
 * CustomGantt component for rendering a Gantt chart with tasks, links, and scales.
 * @param tasks
 * @param links
 * @param scales
 * @param viewMode
 * @param height
 * @param onClickTask
 * @returns {Element}
 * @constructor
 */
const CustomGantt = ({tasks, links, scales, viewMode = "Day", height = "65vh", onClickTask, loading, setLoading, portal, user, onSort, currentSort, onGanttReady, setFpAlreadyExistsModalOpen, setDuplicateFpId, setDuplicateFpName}) => {
    const apiRef = useRef();
    const menuHandler = useRef();

    // German locale configuration with Monday as first day and dd.mm.yyyy format
    const germanLocale = {
        calendar: {
            monthFull: [
                "Januar", "Februar", "März", "April", "Mai", "Juni",
                "Juli", "August", "September", "Oktober", "November", "Dezember"
            ],
            monthShort: [
                "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
                "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"
            ],
            dayFull: [
                "Sonntag", "Montag", "Dienstag", "Mittwoch",
                "Donnerstag", "Freitag", "Samstag"
            ],
            dayShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
            hours: "Stunden",
            minutes: "Minuten",
            done: "Fertig",
            clear: "Löschen",
            today: "Heute",
            weekStart: 1      // Monday is first day of week (0=Sunday, 1=Monday)
        },
        formats: {
            dateFormat: "%d.%m.%Y",  // dd.mm.yyyy format (German standard)
            timeFormat: "%H:%i",
            monthFormat: "%F %Y",     // Full month name and year
            dayFormat: "%d"           // Day number
        }
    };
    // Load saved view mode from localStorage or default to 'week'
    const getSavedViewMode = () => {
        const saved = localStorage.getItem('planungskalender-viewmode');
        return saved && ['workweek', 'week', 'month'].includes(saved) ? saved : 'week';
    };

    const today = new Date();
    // Set total time range to 6 months: 3 months before + 3 months after (fallback)
    const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    const end = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());

    // Helper functions for initial date calculations (defined early)
    const getMonday = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    };

    const getFriday = (date) => {
        const monday = getMonday(date);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        friday.setHours(23, 59, 59, 999);
        return friday;
    };

    const getSunday = (date) => {
        const monday = getMonday(date);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);
        return sunday;
    };

    const getFirstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    };

    const getLastDayOfMonth = (date) => {
        // Get the last day of the month at 23:59:59 to ensure full day is included
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        return lastDay;
    };

    // Calculate initial date range based on saved view mode
    const getInitialDateRange = () => {
        const savedMode = getSavedViewMode();

        switch(savedMode) {
            case 'workweek':
                return {
                    start: getMonday(today),
                    end: getFriday(today)
                };
            case 'week':
                return {
                    start: getMonday(today),
                    end: getSunday(today)
                };
            case 'month':
                return {
                    start: getFirstDayOfMonth(today),
                    end: getLastDayOfMonth(today)
                };
            default:
                return { start, end };
        }
    };

    const initialRange = getInitialDateRange();
    // Filter toolbar buttons to only include "New task" and remove unwanted ones
    const filteredButtons = defaultToolbarButtons.filter(button => {
        // Keep the add-task button (which becomes "Neue Aufgabe")
        const shouldKeep = button.id === "add-task" ||
                          button.type === "add-task" ||
                          button.text === "Neuen Beratungslauf erstellen" ||
                          button.id === "new-task" ||
                          button.type === "new-task";

        return shouldKeep;
    });
    const translatedButtons = translateToolbarButtons(filteredButtons);
    const translatedContextMenu = translateMenuItems(defaultMenuOptions);

    const [currentScales, setScales] = useState(scales);
    const [lengthUnit, setLengthUnit] = useState("day");
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Modal state for consultant selection
    const [consultantModalOpen, setConsultantModalOpen] = useState(false);
    const [modalData, setModalData] = useState({ taskId: null, fromDate: null, toDate: null });

    // Edit mode state for tracking when editing existing tasks
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingTaskData, setEditingTaskData] = useState(null);

    // View mode and navigation state
    const [currentViewMode, setCurrentViewMode] = useState(getSavedViewMode());
    const [currentViewDate, setCurrentViewDate] = useState(today);
    const [viewPeriodText, setViewPeriodText] = useState('');
    const [todayButtonActive, setTodayButtonActive] = useState(false);

    // Dynamic Gantt chart date range based on view mode - initialize with correct dates
    const [ganttStart, setGanttStart] = useState(initialRange.start);
    const [ganttEnd, setGanttEnd] = useState(initialRange.end);

    // Track if Gantt is fully ready
    const [isGanttReady, setIsGanttReady] = useState(false);

    // Use refs for synchronous tracking instead of async state
    const loadedTasksRef = useRef(new Set()); // Track which tasks have already loaded consultants
    const pendingRequestsRef = useRef(new Set()); // Track pending requests
    const editModeRef = useRef({ isEditing: false, taskData: null }); // Immediate edit mode tracking

    // Calculate optimal cell width for 4-week visibility
    const calculateOptimalCellWidth = () => {
        const viewportWidth = window.innerWidth || 1200; // Fallback to 1200px
        // Fixed left panel width: 340 + 76 + 57 + 76 + 236 = 785px (Textspalten-Bereich)
        const leftPanelWidth = 785; // Fixed width of all left grid columns
        const availableWidth = viewportWidth - leftPanelWidth - 50; // 50px for scrollbars/margins
        const daysInFourWeeks = 28;
        const optimalWidth = Math.floor(availableWidth / daysInFourWeeks);

        // Ensure cellWidth is within reasonable bounds (min: 20px, max: 150px)
        return Math.max(20, Math.min(150, optimalWidth));
    };

    const [ cellWidth, setCellWidth ] = useState(calculateOptimalCellWidth());
    const [ scaleHeight, setScaleHeight ] = useState(38);
    const [ cellHeight, setCellHeight ] = useState(36);

    useEffect(() => {
        // Define thresholds for dynamic scale visibility
        const MONTH_SCALE_THRESHOLD = 80; // Show month scale when cellWidth >= 80px

        switch (lengthUnit) {
            case "hour":
                setScales([
                    { unit: "week", step: 1, format: "w" },
                    { unit: "day", step: 1, format: "d-MMM" },
                    { unit: "hour", step: 1, format: "HH:mm" }
                ]);
                break;
            case "day":
                // Dynamic scale visibility based on cellWidth
                const dayScales = [
                    { unit: "week", step: 1, format: "w" },
                    { unit: "day", step: 1, format: "d MMM", css: dayStyle },
                ];
                // Only show month scale when zoomed in enough (cellWidth >= threshold)
                if (cellWidth >= MONTH_SCALE_THRESHOLD) {
                    dayScales.unshift({ unit: "month", step: 1, format: "MMM yyyy" });
                }
                setScales(dayScales);
                break;
            case "week":
                const weekScales = [
                    { unit: "year", step: 1, format: "yyyy" },
                    { unit: "week", step: 1, format: "w" },
                ];
                // Only show month scale when zoomed in enough
                if (cellWidth >= MONTH_SCALE_THRESHOLD) {
                    weekScales.splice(1, 0, { unit: "month", step: 1, format: "MMM yyyy" });
                }
                setScales(weekScales);
                break;
            case "month":
                const monthScales = [
                    { unit: "year", step: 1, format: "yyyy" },
                    { unit: "month", step: 1, format: "MMM yyyy" },
                ];
                // Only show quarter scale when zoomed in enough
                if (cellWidth >= MONTH_SCALE_THRESHOLD) {
                    monthScales.splice(1, 0, { unit: "quarter", step: 1, format: "QQQ yyyy" });
                }
                setScales(monthScales);
                break;
            case "quarter":
                setScales([
                    { unit: "year", step: 1, format: "yyyy" },
                    { unit: "quarter", step: 1, format: "QQQ" },
                ]);
                break;
            default:
                setScales(bigScales);
        }
    }, [lengthUnit, cellWidth]);

    useEffect(() => {
        new Willow({target: document.body});
        document.body.className = `wx-willow-theme`;

        // Add CSS to handle weekend visibility and today highlighting
        const style = document.createElement('style');
        style.innerHTML = `
            /* Gray out weekends */
            .wx-gantt .sday {
                background-color: #f5f5f5 !important;
                opacity: 0.6;
            }

            /* Style for weekends in timeline */
            .wx-timeline-cell.sday {
                background-color: #f0f0f0 !important;
                opacity: 0.5;
            }

            /* For work week mode - create visual separation */
            .workweek-mode .wx-gantt .sday {
                visibility: hidden !important;
                width: 0 !important;
                min-width: 0 !important;
                max-width: 0 !important;
                padding: 0 !important;
                margin: 0 !important;
                border: 0 !important;
            }

            /* Today highlighting - 75% transparent with full column coverage */
            .wx-current-day {
                background-color: rgba(58, 70, 169, 0.25) !important;
                color: #2A3699 !important;
                font-weight: bold !important;
                border-radius: 4px !important;
                position: relative !important;
                z-index: 10 !important;
                border: 1px solid rgba(58, 70, 169, 0.6) !important;
            }

            /* Full column highlighting for today */
            .wx-current-day::before {
                content: '';
                position: absolute;
                top: -50vh;
                bottom: -50vh;
                left: 0;
                right: 0;
                background-color: rgba(58, 70, 169, 0.08) !important;
                z-index: -1;
                pointer-events: none;
            }

            /* Current week highlighting - 75% transparent */
            .wx-current-week {
                background-color: rgba(227, 242, 253, 0.25) !important;
                border-left: 3px solid rgba(58, 70, 169, 0.6) !important;
            }

            /* Month view specific styling */
            .wx-timeline-cell {
                border-right: 1px solid #e0e0e0 !important;
                font-size: 12px !important;
            }

            /* Enhanced today styling for month view - 75% transparent */
            .wx-current-day.wx-timeline-cell {
                background-color: rgba(58, 70, 169, 0.25) !important;
                color: #2A3699 !important;
                font-weight: bold !important;
                border: 2px solid rgba(58, 70, 169, 0.6) !important;
                box-shadow: 0 0 8px rgba(58, 70, 169, 0.2) !important;
            }

            /* Ensure full column highlighting works in all Gantt sections */
            .wx-gantt .wx-current-day::after {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: linear-gradient(to bottom, rgba(58, 70, 169, 0.1), rgba(58, 70, 169, 0.03));
                z-index: -1;
                pointer-events: none;
            }

            /* Past days grayout */
            .wx-past-day {
                background-color: #f8f8f8 !important;
                opacity: 0.7;
                color: #999 !important;
            }

            .wx-past-week {
                background-color: #f8f8f8 !important;
                opacity: 0.8;
            }

            /* Weekend styling */
            .wx-weekend {
                background-color: #f5f5f5 !important;
                opacity: 0.6;
            }

            /* Month view - hide horizontal scrollbar if it appears */
            .month-mode .wx-gantt .wx-timeline-wrapper {
                overflow-x: hidden !important;
            }

            .month-mode .wx-gantt .wx-chart-wrapper {
                overflow-x: hidden !important;
            }

            /* Active button styling with mainBlue color */
            .view-mode-active {
                background-color: #3A46A9 !important;
                color: white !important;
                border-color: #3A46A9 !important;
            }

            /* Today button active state */
            .today-button-active {
                background-color: #3A46A9 !important;
                color: white !important;
                border-color: #3A46A9 !important;
            }

            /* Today button inactive state */
            .today-button-inactive {
                background-color: white !important;
                color: #3A46A9 !important;
                border: 1px solid #3A46A9 !important;
            }

            .today-button-inactive:hover {
                background-color: #f0f4ff !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            if (style.parentNode) {
                style.parentNode.removeChild(style);
            }
        };
    }, []);

    // Handle window resize to maintain optimal 4-week view
    useEffect(() => {
        const handleResize = () => {
            const newOptimalWidth = calculateOptimalCellWidth();
            setCellWidth(newOptimalWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Adjust scroll position when cell width changes to maintain today at left edge
    useEffect(() => {
        if (apiRef.current && tasks.length > 0) {
            setTimeout(() => {
                scrollToTodayLeftEdge();
            }, 50);
        }
    }, [cellWidth]); // Re-run when cell width changes

    // Function to hide columns outside the visible date range
    const hideColumnsOutsideRange = () => {
        if (!ganttStart || !ganttEnd) return;

        // Try multiple times with increasing delays to ensure DOM is ready
        const timeouts = [100, 500, 1000, 1500];

        timeouts.forEach(delay => {
            setTimeout(() => {
                try {
                    // Get start and end dates as timestamps for comparison
                    const startTime = new Date(ganttStart.getFullYear(), ganttStart.getMonth(), ganttStart.getDate()).getTime();
                    const endTime = new Date(ganttEnd.getFullYear(), ganttEnd.getMonth(), ganttEnd.getDate()).getTime();

                    // Find all elements with data-date attribute (covers all column types)
                    const allColumns = document.querySelectorAll('[data-date]');

                    allColumns.forEach(column => {
                        const dateStr = column.getAttribute('data-date');
                        if (dateStr) {
                            try {
                                // Parse the date from data-date attribute
                                const columnDate = new Date(dateStr);
                                const columnTime = new Date(columnDate.getFullYear(), columnDate.getMonth(), columnDate.getDate()).getTime();

                                // Hide columns outside the range
                                if (columnTime < startTime || columnTime > endTime) {
                                    // Use cssText to apply !important styles
                                    column.style.cssText += '; display: none !important; visibility: hidden !important; width: 0 !important; min-width: 0 !important; max-width: 0 !important; padding: 0 !important; margin: 0 !important; border: none !important; overflow: hidden !important;';
                                    // Move element out of view as additional safety
                                    column.setAttribute('data-hidden', 'true');
                                }
                            } catch (e) {
                                // Ignore date parsing errors
                            }
                        }
                    });

                    // Also hide weekend columns in workweek mode
                    if (currentViewMode === 'workweek') {
                        const allCells = document.querySelectorAll('.wx-gantt [class*="cell"], .wx-gantt [class*="col"]');
                        allCells.forEach(cell => {
                            const dayAttr = cell.getAttribute('data-day');
                            if (dayAttr === '6' || dayAttr === '0') {
                                cell.style.display = 'none !important';
                                cell.style.width = '0px !important';
                                cell.style.minWidth = '0px !important';
                                cell.style.maxWidth = '0px !important';
                                cell.style.visibility = 'hidden !important';
                            }
                        });
                    }
                } catch (error) {
                    console.warn('Error hiding columns:', error);
                }
            }, delay);
        });
    };

    // Initialize view period text and dates on component mount
    useEffect(() => {
        const settings = calculateViewSettings(currentViewMode, currentViewDate);
        if (settings) {
            setViewPeriodText(settings.periodText);
            setCellWidth(settings.cellWidth);

            // Set initial Gantt date range based on view mode
            // Initialize with today's period, but navigation will change this
            if (currentViewMode === 'workweek') {
                const currentMonday = getMonday(currentViewDate);
                const currentFriday = getFriday(currentViewDate);
                setGanttStart(currentMonday);
                setGanttEnd(currentFriday);
            } else if (currentViewMode === 'week') {
                const currentMonday = getMonday(currentViewDate);
                const currentSunday = getSunday(currentViewDate);
                setGanttStart(currentMonday);
                setGanttEnd(currentSunday);
            } else if (currentViewMode === 'month') {
                const currentMonthFirst = getFirstDayOfMonth(currentViewDate);
                const currentMonthLast = getLastDayOfMonth(currentViewDate);
                setGanttStart(currentMonthFirst);
                setGanttEnd(currentMonthLast);
            }
        }

        // Hide columns after initial setup
        hideColumnsOutsideRange();
    }, []); // Run once on mount

    // Re-hide columns when view mode or date range changes
    useEffect(() => {
        hideColumnsOutsideRange();
    }, [currentViewMode, ganttStart, ganttEnd, tasks.length]);

    // Set initial scroll position to position today at the very left edge of viewport
    useEffect(() => {
        if (apiRef.current && tasks.length > 0) {
            // Longer delay to ensure Gantt chart is fully rendered
            const scrollTimer = setTimeout(() => {
                // Get the start date based on current view mode
                const settings = calculateViewSettings(currentViewMode, currentViewDate);
                if (settings) {
                    scrollToDate(settings.startDate);
                }

                // Re-apply view settings after Gantt is loaded
                hideColumnsOutsideRange();
            }, 1000); // Increased delay for better reliability

            return () => clearTimeout(scrollTimer);
        }
    }, [tasks.length]); // Re-run when tasks are loaded

    // Helper functions for date calculations (moved to top for initial range calculation)

    const getLastDayOfMonthActual = (date) => {
        // Get the actual last day without adding extra day (for display purposes)
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };

    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
        return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
    };

    const formatDateRange = (startDate, endDate, mode) => {
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                           'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

        if (mode === 'workweek' || mode === 'week') {
            const weekNum = getWeekNumber(startDate);
            const startDay = startDate.getDate();
            const endDay = endDate.getDate();
            const month = monthNames[startDate.getMonth()];
            const year = startDate.getFullYear();

            if (startDate.getMonth() === endDate.getMonth()) {
                return `${startDay}–${endDay} ${month}, ${year} (Woche ${weekNum})`;
            } else {
                const endMonth = monthNames[endDate.getMonth()];
                return `${startDay} ${month} – ${endDay} ${endMonth}, ${year} (Woche ${weekNum})`;
            }
        } else if (mode === 'month') {
            return `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`;
        }
    };

    // Calculate visible date range and optimal zoom based on view mode
    const calculateViewSettings = (mode, referenceDate = currentViewDate) => {
        let startDate, endDate, optimalCellWidth;
        const viewportWidth = window.innerWidth || 1200;
        // Actual left panel width: 340 + 76 + 57 + 76 + 236 = 785px
        const leftPanelWidth = 785;
        // Account for minimal margin (20px) to allow calendar to extend to screen edge
        const availableWidth = viewportWidth - leftPanelWidth - 20;

        switch(mode) {
            case 'workweek':
                // For work week, show EXACTLY Monday to Friday of the REFERENCE week
                // Use referenceDate to allow navigation, not today
                const currentWeekMonday = getMonday(referenceDate);
                const currentWeekFriday = getFriday(referenceDate);

                startDate = currentWeekMonday;
                endDate = currentWeekFriday;

                // Calculate cell width to fit exactly 5 days (Mon-Fri) in viewport
                // Reduce by 2% to account for borders and spacing
                optimalCellWidth = Math.floor((availableWidth / 5) * 0.98);
                break;
            case 'week':
                // For full week, show EXACTLY Monday to Sunday of the REFERENCE week
                // Use referenceDate to allow navigation, not today
                const currentWeekMondayFull = getMonday(referenceDate);
                const currentWeekSundayFull = getSunday(referenceDate);

                startDate = currentWeekMondayFull;
                endDate = currentWeekSundayFull;

                // Calculate cell width to fit exactly 7 days (Mon-Sun) in viewport
                // Reduce by 2% to account for borders and spacing
                optimalCellWidth = Math.floor((availableWidth / 7) * 0.98);
                break;
            case 'month':
                // For month, show EXACTLY 1st to last day of the REFERENCE month
                // Use referenceDate to allow navigation, not today
                const currentMonthFirst = getFirstDayOfMonth(referenceDate);
                const currentMonthLast = getLastDayOfMonth(referenceDate);

                startDate = currentMonthFirst;
                endDate = currentMonthLast;

                // Calculate cell width to fit exactly all days of reference month in viewport
                const actualLastDay = getLastDayOfMonthActual(referenceDate);
                const daysInCurrentMonth = actualLastDay.getDate();
                // Reduce by 3% to account for borders, spacing, and ensure all days fit
                optimalCellWidth = Math.floor((availableWidth / daysInCurrentMonth) * 0.97);
                break;
            default:
                return null;
        }

        // Ensure cellWidth is within reasonable bounds
        const minWidth = (mode === 'workweek') ? 120 : (mode === 'week') ? 80 : (mode === 'month') ? 25 : 40; // Smaller minimum for month to fit all days
        const maxWidth = (mode === 'workweek') ? 300 : (mode === 'week') ? 250 : (mode === 'month') ? 70 : 200; // Smaller maximum for month
        optimalCellWidth = Math.max(minWidth, Math.min(maxWidth, optimalCellWidth));

        return {
            startDate,
            endDate,
            cellWidth: optimalCellWidth,
            periodText: formatDateRange(startDate, endDate, mode)
        };
    };

    // Handle view mode button clicks
    const handleViewModeChange = (mode) => {
        setCurrentViewMode(mode);
        // Save to localStorage for persistence
        localStorage.setItem('planungskalender-viewmode', mode);
        // Reset today button active state when changing view
        setTodayButtonActive(false);

        const settings = calculateViewSettings(mode, currentViewDate);
        if (settings) {
            setCellWidth(settings.cellWidth);
            setViewPeriodText(settings.periodText);

            // Set precise Gantt chart date range based on view mode and current view date
            if (mode === 'workweek') {
                // For work week, set Gantt to show exactly Mon-Fri of reference week
                const currentMonday = getMonday(currentViewDate);
                const currentFriday = getFriday(currentViewDate);
                setGanttStart(currentMonday);
                setGanttEnd(currentFriday);
            } else if (mode === 'week') {
                // For full week, set Gantt to show exactly Mon-Sun of reference week
                const currentMonday = getMonday(currentViewDate);
                const currentSunday = getSunday(currentViewDate);
                setGanttStart(currentMonday);
                setGanttEnd(currentSunday);
            } else if (mode === 'month') {
                // For month, set Gantt to show exactly 1st to last day of reference month
                const currentMonthFirst = getFirstDayOfMonth(currentViewDate);
                const currentMonthLast = getLastDayOfMonth(currentViewDate);
                setGanttStart(currentMonthFirst);
                setGanttEnd(currentMonthLast);
            } else {
                // For other modes, use the default 6-month range
                setGanttStart(start);
                setGanttEnd(end);
            }

            // Scroll to the start date after a short delay
            setTimeout(() => {
                scrollToDate(settings.startDate);

                // For work week, week, and month ensure exact positioning
                if (mode === 'workweek' || mode === 'week') {
                    // Position Monday of current view date at the very left edge
                    scrollToDate(getMonday(currentViewDate));
                } else if (mode === 'month') {
                    // Position 1st day of current view month at the very left edge
                    scrollToDate(getFirstDayOfMonth(currentViewDate));
                }
            }, 200);
        }
    };

    // Limit scroll for work week view to hide weekends
    const limitWorkWeekScroll = () => {
        try {
            // Find the scrollable container
            const scrollContainers = document.querySelectorAll('.wx-gantt-timeline, .wx-timeline-wrapper, .wx-chart-wrapper');
            scrollContainers.forEach(container => {
                if (container.scrollWidth > container.clientWidth) {
                    // Calculate max scroll to show only Mon-Fri
                    const daysVisible = 5; // Monday to Friday
                    const maxScroll = (daysVisible * cellWidth);

                    // Add scroll limiter
                    container.addEventListener('scroll', function(e) {
                        if (currentViewMode === 'workweek') {
                            const currentScroll = container.scrollLeft;
                            const mondayPosition = getMonday(currentViewDate);
                            const fridayPosition = getFriday(currentViewDate);
                            const daysBetweenStartAndMonday = Math.floor((mondayPosition.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                            const daysBetweenStartAndFriday = Math.floor((fridayPosition.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));

                            const minScrollPosition = daysBetweenStartAndMonday * cellWidth;
                            const maxScrollPosition = (daysBetweenStartAndFriday - 4) * cellWidth; // Friday minus viewport

                            // Prevent scrolling beyond Friday
                            if (currentScroll > maxScrollPosition) {
                                container.scrollLeft = maxScrollPosition;
                            }
                        }
                    });
                }
            });
        } catch (error) {
            console.error('Error limiting work week scroll:', error);
        }
    };

    // Handle navigation arrows
    const handleNavigate = (direction) => {
        let newDate = new Date(currentViewDate);

        switch(currentViewMode) {
            case 'workweek':
            case 'week':
                newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
                break;
        }

        setCurrentViewDate(newDate);
        const settings = calculateViewSettings(currentViewMode, newDate);
        if (settings) {
            setViewPeriodText(settings.periodText);

            // Update Gantt date range based on the new reference date
            if (currentViewMode === 'workweek') {
                const mondayRef = getMonday(newDate);
                const fridayRef = getFriday(newDate);
                setGanttStart(mondayRef);
                setGanttEnd(fridayRef);
            } else if (currentViewMode === 'week') {
                const mondayRef = getMonday(newDate);
                const sundayRef = getSunday(newDate);
                setGanttStart(mondayRef);
                setGanttEnd(sundayRef);
            } else if (currentViewMode === 'month') {
                const monthFirstRef = getFirstDayOfMonth(newDate);
                const monthLastRef = getLastDayOfMonth(newDate);
                setGanttStart(monthFirstRef);
                setGanttEnd(monthLastRef);
            }

            setTimeout(() => {
                scrollToDate(settings.startDate);
            }, 100);
        }
    };

    // Handle Today button click
    const handleTodayClick = () => {
        setTodayButtonActive(true);
        // Set view date to today to show the period containing today
        setCurrentViewDate(today);

        const settings = calculateViewSettings(currentViewMode, today);
        if (settings) {
            setCellWidth(settings.cellWidth);
            setViewPeriodText(settings.periodText);

            // Update Gantt date range to show the period containing today
            if (currentViewMode === 'workweek') {
                const todayMonday = getMonday(today);
                const todayFriday = getFriday(today);
                setGanttStart(todayMonday);
                setGanttEnd(todayFriday);
            } else if (currentViewMode === 'week') {
                const todayMonday = getMonday(today);
                const todaySunday = getSunday(today);
                setGanttStart(todayMonday);
                setGanttEnd(todaySunday);
            } else if (currentViewMode === 'month') {
                const todayMonthFirst = getFirstDayOfMonth(today);
                const todayMonthLast = getLastDayOfMonth(today);
                setGanttStart(todayMonthFirst);
                setGanttEnd(todayMonthLast);
            }

            // Scroll to show today in the viewport
            setTimeout(() => {
                scrollToTodayInViewport();
            }, 200);
        }

        // Reset today button active state after a delay
        setTimeout(() => {
            setTodayButtonActive(false);
        }, 2000); // Active for 2 seconds
    };

    // Function to scroll so today is visible in the viewport
    const scrollToTodayInViewport = () => {
        try {
            let targetScrollDate;

            switch(currentViewMode) {
                case 'workweek':
                    // For work week, check if today is a weekday (Mon-Fri)
                    const todayDayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
                    const workWeekMonday = getMonday(today);

                    if (todayDayOfWeek >= 1 && todayDayOfWeek <= 5) {
                        // Today is Monday-Friday, position Monday at left edge
                        targetScrollDate = workWeekMonday;
                    } else {
                        // Today is weekend, show the work week but scroll to show all 5 days
                        targetScrollDate = workWeekMonday;
                    }
                    break;
                case 'week':
                    // For full week, position Monday at left edge so today is visible
                    const weekMonday = getMonday(today);
                    targetScrollDate = weekMonday;
                    break;
                case 'month':
                    // For month view, ensure today is visible
                    const monthFirst = getFirstDayOfMonth(today);
                    const monthLast = getLastDayOfMonthActual(today);
                    const daysInMonth = monthLast.getDate();
                    const todayOfMonth = today.getDate();

                    // Calculate viewport capacity
                    const viewportWidth = window.innerWidth || 1200;
                    const leftPanelWidth = 350;
                    const availableWidth = viewportWidth - leftPanelWidth - 80; // Extra margin for safety
                    const visibleDays = Math.floor(availableWidth / cellWidth);

                    if (visibleDays >= daysInMonth) {
                        // All days fit in viewport, start from 1st
                        targetScrollDate = monthFirst;
                    } else {
                        // Need to scroll to show today
                        const centerOffset = Math.floor(visibleDays / 2);
                        let targetDay = todayOfMonth - centerOffset;

                        // Ensure we don't scroll past the beginning or end of month
                        targetDay = Math.max(1, targetDay);
                        if (targetDay + visibleDays > daysInMonth) {
                            targetDay = daysInMonth - visibleDays + 1;
                        }
                        targetDay = Math.max(1, targetDay);

                        targetScrollDate = new Date(monthFirst.getTime() + ((targetDay - 1) * 24 * 60 * 60 * 1000));
                    }
                    break;
                default:
                    targetScrollDate = today;
            }

            scrollToDate(targetScrollDate);
        } catch (error) {
            console.error('Error scrolling to today:', error);
            // Fallback to simple scroll to today
            scrollToDate(today);
        }
    };

    // Generic scroll to date function
    const scrollToDate = (targetDate) => {
        try {
            // Calculate the number of days between gantt start date and target
            const daysBetween = Math.floor((targetDate.getTime() - ganttStart.getTime()) / (24 * 60 * 60 * 1000));
            const scrollPosition = daysBetween * cellWidth;

            // Use the same scroll logic as scrollToTodayLeftEdge
            const possibleSelectors = [
                '.wx-gantt-chart-wrapper',
                '.wx-chart-wrapper',
                '.wx-gantt-wrapper',
                '.wx-gantt',
                '.wx-timeline',
                '.wx-gantt-timeline',
                '.wx-timeline-wrapper',
                '.wx-timeline-container',
                '.wx-chart',
                '.wx-gantt-chart',
                '.wx-gantt-body',
                '.wx-gantt-view',
                '.wx-gantt-container',
                '[class*="timeline"]',
                '[class*="gantt-chart"]',
                '[class*="scrollable"]'
            ];

            let scrollElementFound = false;

            for (const selector of possibleSelectors) {
                const elements = document.querySelectorAll(selector);
                elements.forEach(element => {
                    if (element.scrollWidth > element.clientWidth && !scrollElementFound) {
                        element.scrollLeft = scrollPosition;
                        scrollElementFound = true;
                    }
                });
                if (scrollElementFound) break;
            }

            // Alternative: Look for the main Gantt component
            if (!scrollElementFound) {
                const ganttWrapper = document.querySelector('.planung-kalender-wrapper');
                if (ganttWrapper) {
                    const allElements = ganttWrapper.getElementsByTagName('*');
                    for (let element of allElements) {
                        if (element.scrollWidth > element.clientWidth + 10) {
                            element.scrollLeft = scrollPosition;
                            setTimeout(() => {
                                element.scrollLeft = scrollPosition;
                            }, 100);
                            break;
                        }
                    }
                }
            }

            // Try API method as fallback
            if (apiRef.current) {
                try {
                    apiRef.current.exec("scroll-to-date", { date: targetDate });
                } catch (error) {
                    // Silent fail
                }
            }
        } catch (error) {
            console.error('Error scrolling to date:', error);
        }
    };

    // Function to precisely position today at the left edge of the viewport
    const scrollToTodayLeftEdge = () => {
        try {
            // Calculate the number of days between start date and today
            const daysBetween = Math.floor((today.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));

            // Calculate the scroll position (days * cell width)
            const scrollPosition = daysBetween * cellWidth;

            // Enhanced selectors including wx-react-gantt specific elements
            const possibleSelectors = [
                '.wx-gantt-chart-wrapper',
                '.wx-chart-wrapper',
                '.wx-gantt-wrapper',
                '.wx-gantt',
                '.wx-timeline',
                '.wx-gantt-timeline',
                '.wx-timeline-wrapper',
                '.wx-timeline-container',
                '.wx-chart',
                '.wx-gantt-chart',
                '.wx-gantt-body',
                '.wx-gantt-view',
                '.wx-gantt-container',
                '[class*="timeline"]',
                '[class*="gantt-chart"]',
                '[class*="scrollable"]'
            ];

            let scrollElementFound = false;

            // Try each selector
            for (const selector of possibleSelectors) {
                const elements = document.querySelectorAll(selector);
                elements.forEach(element => {
                    // Check if element is horizontally scrollable
                    if (element.scrollWidth > element.clientWidth && !scrollElementFound) {
                        element.scrollLeft = scrollPosition;
                        scrollElementFound = true;
                    }
                });
                if (scrollElementFound) break;
            }

            // Alternative: Look for the main Gantt component and find scrollable children
            if (!scrollElementFound) {
                const ganttWrapper = document.querySelector('.planung-kalender-wrapper');
                if (ganttWrapper) {
                    // Get all elements and filter for horizontally scrollable ones
                    const allElements = ganttWrapper.getElementsByTagName('*');
                    for (let element of allElements) {
                        if (element.scrollWidth > element.clientWidth + 10) { // +10 for tolerance
                            element.scrollLeft = scrollPosition;
                            scrollElementFound = true;

                            // Double-check by setting it again after a short delay
                            setTimeout(() => {
                                element.scrollLeft = scrollPosition;
                            }, 100);
                            break;
                        }
                    }
                }
            }

            // Final attempt: Use the Gantt API with today's date
            if (apiRef.current) {
                setTimeout(() => {
                    try {
                        apiRef.current.exec("scroll-to-date", {
                            date: today,
                            position: "start"
                        });
                    } catch (apiError) {
                        // Final API scroll attempt failed
                    }
                }, 200);
            }
        } catch (error) {
            // Error in scrollToTodayLeftEdge
        }
    };

    useEffect(() => {
        import('wx-react-gantt/dist/gantt.css');
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.svar.dev/fonts/wxi/wx-icons.css';
        document.head.appendChild(link);

        // Add custom CSS for datepicker styling
        const style = document.createElement('style');
        style.id = 'gantt-datepicker-german';
        style.textContent = `
            /* Ensure calendar grid layout */
            .wx-portal .wx-calendar .wx-days {
                display: grid !important;
                grid-template-columns: repeat(7, 1fr) !important;
            }
        `;
        document.head.appendChild(style);

        // Function to convert date inputs
        const convertDateInputs = () => {
            const dateInputs = document.querySelectorAll('.wx-sidebar input[type="text"]');
            dateInputs.forEach((input) => {
                const value = input.value;
                // Convert mm/dd/yyyy to dd.mm.yyyy
                if (value && value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    const [month, day, year] = value.split('/');
                    const formatted = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
                    if (input.value !== formatted) {
                        input.value = formatted;
                        input.setAttribute('data-iso-value', `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                    }
                }
                // Set placeholder
                if (!input.placeholder || input.placeholder.includes('/')) {
                    input.placeholder = 'TT.MM.JJJJ';
                }
            });
        };

        // Watch for date inputs to convert format
        const observer = new MutationObserver(() => {
            convertDateInputs();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false
        });

        // Run initial conversion after a short delay
        setTimeout(() => {
            convertDateInputs();
        }, 500);

        // Apply working day styles after Gantt is loaded
        setTimeout(() => {
            applyWorkingDayStyles();
        }, 500);

        return () => {
            document.head.removeChild(link);
            document.head.removeChild(style);
            observer.disconnect();
        };
    }, []);

    // Re-apply working day styles when cellWidth or viewMode changes
    useEffect(() => {
        applyWorkingDayStyles();
    }, [cellWidth, currentViewMode]);

    function isDayOff(date) {
        const d = date.getDay();
        return d === 0 || d === 6;
    }

    function isHourOff(date) {
        const h = date.getHours();
        return h < 8 || h === 12 || h > 17;
    }

    function highlightTime(d, u) {
        const currentDate = new Date();
        const targetDate = new Date(d);

        // Normalize dates to compare only year, month, and day
        const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

        // Check if it's today
        const isToday = currentDateOnly.getTime() === targetDateOnly.getTime();

        // Check if it's in the past
        const isPast = targetDateOnly.getTime() < currentDateOnly.getTime();

        // For week unit, check if current week contains today
        const isCurrentWeek = u === "week" && isDateInCurrentWeek(targetDate, currentDate);

        // Priority order: today > current week > past > weekend
        if (u === "day" && isToday) return "wx-current-day";
        if (u === "week" && isCurrentWeek) return "wx-current-week";
        if (u === "day" && isPast) return "wx-past-day";
        if (u === "week" && isPast) return "wx-past-week";
        if (u === "day" && isDayOff(d)) return "wx-weekend";
        if (u === "hour" && (isDayOff(d) || isHourOff(d))) return "wx-weekend";

        return "";
    }

    // Helper function to check if a date is in the current week
    function isDateInCurrentWeek(targetDate, currentDate) {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Start of week (Sunday)
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
        endOfWeek.setHours(23, 59, 59, 999);

        return targetDate >= startOfWeek && targetDate <= endOfWeek;
    }

    // Track if events are already attached to prevent duplicates
    const eventsAttachedRef = useRef(false);
    const sidebarInjectedRef = useRef(false);
    const processedTempTasksRef = useRef(new Set()); // Track processed temporary tasks
    const addingConsultantsRef = useRef(false); // Track when adding consultant subtasks
    const clickHandlersAttachedRef = useRef(false); // Track if click handlers are already attached
    const sidebarTransitionRef = useRef(false); // Track sidebar transition to prevent race conditions

    useEffect(() => {
        if (apiRef.current && !eventsAttachedRef.current) {
            eventsAttachedRef.current = true;

            // Only use the primary event for lazy loading to avoid duplicates
            apiRef.current.on("request-data", ({ id }) => {
                // Ignore request-data events for message tasks
                if (id && id.toString().startsWith('no_consultants_')) {
                    return;
                }
                getFPConsultants(id);
            });

            // Monitor DOM to update toggle icon state after Gantt operations
            setTimeout(() => {
                const ganttContainer = document.querySelector('.wx-gantt');
                if (ganttContainer) {
                    // Add global unhandled rejection handler to suppress expected Gantt library errors
                    // These errors occur during batch task deletions when the library tries to access parent properties
                    // They don't affect functionality - tasks are still deleted successfully
                    const originalUnhandledRejection = window.onunhandledrejection;
                    window.addEventListener('unhandledrejection', (event) => {
                        const error = event.reason;
                        // Suppress expected parent-related errors from Gantt library during task deletion
                        if (error && (
                            error.message?.includes('parent') ||
                            error.message?.includes('undefined') ||
                            error.toString().includes('Cannot read properties of undefined (reading \'parent\')') ||
                            error.toString().includes('parent')
                        )) {
                            // These are expected and harmless - prevent them from showing in console
                            event.preventDefault();
                            return;
                        }
                        // Allow other errors to bubble up
                        if (originalUnhandledRejection) {
                            originalUnhandledRejection(event);
                        }
                    });

                    // Add click handler for toggle icons to handle expand/collapse
                    const handleToggleClick = async (event) => {
                        const toggleIcon = event.target.closest('.wx-toggle-icon');
                        if (!toggleIcon) return;

                        const row = toggleIcon.closest('[data-id]');
                        if (!row) return;

                        const taskId = row.getAttribute('data-id');
                        if (!taskId) return;

                        // Check if task is currently expanded
                        const isExpanded = toggleIcon.classList.contains('wx-custom-expanded');
                        
                        if (isExpanded) {
                            // Task is expanded, collapse it
                            event.preventDefault();
                            event.stopPropagation();
                            
                            try {
                                // Collect all child task IDs first from both DOM and tasks array
                                const childTaskIdsToDelete = new Set();

                                // Find all child rows in the DOM (consultants, message, add button)
                                const gridBody = ganttContainer.querySelector('.wx-body');
                                if (gridBody) {
                                    // Get the parent row index
                                    const allRows = Array.from(gridBody.querySelectorAll('[data-id]'));
                                    const parentRowIndex = allRows.findIndex(r => r.getAttribute('data-id') === taskId);
                                    
                                    if (parentRowIndex !== -1) {
                                        // Find all child rows (rows immediately after parent that are children)
                                        for (let i = parentRowIndex + 1; i < allRows.length; i++) {
                                            const row = allRows[i];
                                            const rowTaskId = row.getAttribute('data-id');

                                            if (!rowTaskId) continue;

                                            // Check if this row is a child (consultant, message, or button)
                                            const isMessage = row.hasAttribute('data-message-for') &&
                                                            row.getAttribute('data-message-for') === taskId;

                                            // Check if it's a child task by checking task parent or task type
                                            const taskInArray = tasks.find(t => t.id.toString() === rowTaskId.toString());
                                            const isChild = taskInArray &&
                                                           ((taskInArray.parent && taskInArray.parent.toString() === taskId) ||
                                                            taskInArray.type === 'consultant' ||
                                                            taskInArray.type === 'add-button');

                                            // Also check by data attributes on the row
                                            const rowIsConsultant = row.querySelector('[data-task-type="consultant"]') ||
                                                                   row.getAttribute('data-task-type') === 'consultant' ||
                                                                   rowTaskId.includes('_consultant_');

                                            if (isMessage || isChild || rowIsConsultant) {
                                                childTaskIdsToDelete.add(rowTaskId);
                                            } else {
                                                // Stop at first non-child row (same level or higher)
                                                // Check if this row is indented less than the parent
                                                const rowIndent = row.querySelector('.wx-cell')?.style.paddingLeft || '0px';
                                                const parentIndent = allRows[parentRowIndex].querySelector('.wx-cell')?.style.paddingLeft || '0px';
                                                if (parseInt(rowIndent) <= parseInt(parentIndent)) {
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                }

                                // Also check tasks array for any consultant tasks we might have missed
                                const consultantTasks = tasks.filter(task => 
                                    task.parent && task.parent.toString() === taskId.toString()
                                );
                                
                                consultantTasks.forEach(consultantTask => {
                                    childTaskIdsToDelete.add(consultantTask.id.toString());
                                });

                                // Also check for message tasks and add button tasks by their IDs
                                const messageTaskId = `no_consultants_${taskId}`;
                                const addButtonTaskId = `add_consultant_button_${taskId}`;
                                childTaskIdsToDelete.add(messageTaskId);
                                childTaskIdsToDelete.add(addButtonTaskId);

                                // Delete all child tasks sequentially with a small delay between each
                                // Delete in reverse order (most nested first) to avoid parent reference issues
                                const deletePromises = [];
                                const childTaskIdsArray = Array.from(childTaskIdsToDelete);
                                let delay = 0;
                                
                                // Delete in reverse order to avoid parent reference errors
                                for (let i = childTaskIdsArray.length - 1; i >= 0; i--) {
                                    const childTaskId = childTaskIdsArray[i];
                                    deletePromises.push(
                                        new Promise((resolve) => {
                                            setTimeout(async () => {
                                                try {
                                                    // Check if task still exists before trying to delete
                                                    const taskExists = tasks.some(t => t.id.toString() === childTaskId.toString());
                                                    if (taskExists || childTaskId.includes('_consultant_') || 
                                                        childTaskId.startsWith('no_consultants_') || 
                                                        childTaskId.startsWith('add_consultant_')) {
                                                        // Try to delete - use async/await to properly catch promise rejections
                                                        try {
                                                            const result = apiRef.current.exec("delete-task", { id: childTaskId });
                                                            
                                                            // If exec returns a promise, await it to catch rejections
                                                            if (result && typeof result.then === 'function') {
                                                                await result.catch((promiseErr) => {
                                                                    // Suppress expected parent-related errors during batch deletions
                                                                    // These are safe to ignore - the deletion still works
                                                                    const isParentError = promiseErr && (
                                                                        promiseErr.message?.includes('parent') ||
                                                                        promiseErr.message?.includes('undefined') ||
                                                                        promiseErr.toString().includes('parent') ||
                                                                        promiseErr.toString().includes('Cannot read properties')
                                                                    );
                                                                    // Silently suppress expected parent errors - they don't affect functionality
                                                                    if (!isParentError) {
                                                                        console.warn(`Note: delete-task promise rejected for ${childTaskId}:`, promiseErr.message || promiseErr);
                                                                    }
                                                                    // Return undefined to resolve the promise chain
                                                                    return undefined;
                                                                });
                                                            }
                                                            
                                                            console.log(`✓ Deleted child task: ${childTaskId}`);
                                                        } catch (deleteErr) {
                                                            // Handle synchronous errors
                                                            const isParentError = deleteErr && (
                                                                deleteErr.message?.includes('parent') ||
                                                                deleteErr.message?.includes('undefined') ||
                                                                deleteErr.toString().includes('parent') ||
                                                                deleteErr.toString().includes('Cannot read properties')
                                                            );
                                                            if (!isParentError) {
                                                                console.warn(`Note: delete-task failed for ${childTaskId}:`, deleteErr.message || deleteErr);
                                                            }
                                                        }
                                                    }
                                                } catch (err) {
                                                    // Outer error handler for any unexpected errors
                                                    console.warn(`Error processing deletion for ${childTaskId}:`, err);
                                                }
                                                resolve();
                                            }, delay);
                                            delay += 20; // Increased delay to 20ms to give Gantt library time to process
                                        })
                                    );
                                }

                                // Wait for all deletions to complete (but don't fail if some have errors)
                                await Promise.allSettled(deletePromises);

                                // Force remove any remaining child rows from DOM if they weren't deleted
                                setTimeout(() => {
                                    const remainingRows = gridBody ? Array.from(gridBody.querySelectorAll('[data-id]')) : [];
                                    const parentRowIndexAfter = remainingRows.findIndex(r => r.getAttribute('data-id') === taskId);
                                    
                                    if (parentRowIndexAfter !== -1) {
                                        // Check if there are still child rows that should be removed
                                        for (let i = parentRowIndexAfter + 1; i < remainingRows.length; i++) {
                                            const row = remainingRows[i];
                                            const rowTaskId = row.getAttribute('data-id');

                                            if (!rowTaskId) continue;

                                            // Check if this is still a child row
                                            const isStillChild = rowTaskId.includes('_consultant_') ||
                                                                rowTaskId === `no_consultants_${taskId}` ||
                                                                rowTaskId === `add_consultant_button_${taskId}` ||
                                                                row.hasAttribute('data-message-for') && row.getAttribute('data-message-for') === taskId;

                                            if (isStillChild) {
                                                // Force remove from DOM
                                                console.log(`Force removing remaining child row: ${rowTaskId}`);
                                                row.remove();
                                            } else {
                                                // Stop at first non-child row
                                                break;
                                            }
                                        }
                                    }
                                }, 200); // Wait 200ms for Gantt to process deletions

                                // Clear the loaded tasks ref to allow re-expansion
                                // IMPORTANT: We clear this so the task can be loaded again
                                if (loadedTasksRef.current) {
                                    loadedTasksRef.current.delete(taskId);
                                }
                                if (pendingRequestsRef.current) {
                                    pendingRequestsRef.current.delete(taskId);
                                }
                                
                                // Mark that this task was previously loaded, so we know to manually trigger on re-expand
                                // Store this in a separate ref that persists across collapse/expand cycles
                                if (!window.collapsedTasksRef) {
                                    window.collapsedTasksRef = new Set();
                                }
                                window.collapsedTasksRef.add(taskId);

                                // IMPORTANT: Reset task's state after collapse to allow re-expansion
                                // Wait a bit for deletions to complete, then reset the task state
                                setTimeout(() => {
                                    const taskToReset = tasks.find(task => task.id.toString() === taskId.toString());
                                    if (taskToReset) {
                                        // Reset task state to allow re-expansion
                                        // Use a fresh task object to avoid parent reference errors
                                        const resetTask = {
                                            id: taskToReset.id,
                                            text: taskToReset.text,
                                            start: taskToReset.start,
                                            end: taskToReset.end,
                                            duration: taskToReset.duration,
                                            progress: taskToReset.progress || null,
                                            type: "summary", // Must be summary for expandable behavior
                                            lazy: true, // Critical: must be lazy to trigger request-data
                                            open: false, // Must be false to allow expansion
                                            Links: taskToReset.Links || [],
                                            details: taskToReset.details || ''
                                        };
                                        
                                        try {
                                            // Try to update with minimal task object to avoid parent errors
                                            const updateResult = apiRef.current.exec("update-task", {
                                                id: taskId,
                                                task: resetTask
                                            });
                                            
                                            // Handle promise if exec returns one
                                            if (updateResult && typeof updateResult.catch === 'function') {
                                                updateResult.catch((err) => {
                                                    // Suppress expected errors - the reset still works
                                                    console.warn(`Note: update-task promise error (expected): ${err.message || err}`);
                                                });
                                            }
                                            
                                            console.log(`✓ Reset task ${taskId} to collapsed/lazy state for re-expansion`);
                                        } catch (updateErr) {
                                            // If update fails, that's okay - we've cleared refs and deleted children
                                            // The task should still be expandable on next click
                                            console.warn('Note: Could not reset task state (may still work):', updateErr.message || updateErr);
                                        }
                                    } else {
                                        console.warn(`Task ${taskId} not found in tasks array for reset`);
                                    }
                                }, 300); // Wait longer for deletions to fully complete and DOM to stabilize
                                
                                // Update toggle icon state immediately
                                toggleIcon.classList.remove('wx-custom-expanded');
                                
                                // Force a small delay to let the DOM update, then trigger MutationObserver
                                setTimeout(() => {
                                    // This ensures the MutationObserver sees the change
                                    const gridBody = ganttContainer.querySelector('.wx-body');
                                    if (gridBody) {
                                        // Trigger a synthetic mutation to ensure observer runs
                                        const observer = new MutationObserver(() => {});
                                        observer.observe(gridBody, { childList: true, subtree: true });
                                        setTimeout(() => observer.disconnect(), 100);
                                    }
                                }, 50);
                            } catch (error) {
                                console.error('Error closing task:', error);
                            }
                        } else {
                            // Task is collapsed - verify it's actually collapsed before allowing expansion
                            // Sometimes the class might be removed but the task state isn't reset
                            const taskInState = tasks.find(t => t.id.toString() === taskId.toString());
                            const isActuallyCollapsed = !taskInState || 
                                                       (!taskInState.open && 
                                                        (!loadedTasksRef.current || !loadedTasksRef.current.has(taskId)));
                            
                            if (isActuallyCollapsed) {
                                // Task is truly collapsed - allow default behavior to proceed
                                // This will trigger the Gantt library's default expand action
                                // which fires the "request-data" event for lazy loading
                                // DON'T prevent default - we want the normal expand flow
                                console.log(`Task ${taskId} is collapsed, allowing default expand`);
                                return; // Exit early to allow default behavior
                            } else {
                                // Task state might be inconsistent - force collapse first
                                console.log(`Task ${taskId} state inconsistent, forcing collapse reset`);
                                try {
                                    if (taskInState) {
                                        apiRef.current.exec("update-task", {
                                            id: taskId,
                                            task: { ...taskInState, open: false, lazy: true }
                                        });
                                    }
                                } catch (err) {
                                    console.warn('Could not reset task state:', err);
                                }
                                // Still allow default behavior after reset attempt
                                return;
                            }
                        }
                    };

                    // Add event listener to toggle icons using event delegation
                    // Use capture phase ONLY for expanded tasks (to intercept collapse)
                    // For collapsed tasks, check if we need to manually trigger request-data
                    ganttContainer.addEventListener('click', (event) => {
                        const toggleIcon = event.target.closest('.wx-toggle-icon') || 
                                          (event.target.classList.contains('wx-toggle-icon') ? event.target : null);
                        
                        if (!toggleIcon) return;
                        
                        const row = toggleIcon.closest('[data-id]');
                        if (!row) return;
                        
                        const taskId = row.getAttribute('data-id');
                        if (!taskId) return;
                        
                        // Check if task is currently expanded by looking at the icon class
                        const isExpanded = toggleIcon.classList.contains('wx-custom-expanded');
                        
                        if (isExpanded) {
                            // Task is expanded - intercept in capture phase to handle collapse
                            handleToggleClick(event);
                            // handleToggleClick will preventDefault and stopPropagation
                        } else {
                            // Task is collapsed - check if it was previously loaded and collapsed
                            // If it was collapsed after expansion, manually trigger request-data
                            // because the Gantt library might not trigger it automatically
                            const wasPreviouslyCollapsed = window.collapsedTasksRef && window.collapsedTasksRef.has(taskId);
                            
                            if (wasPreviouslyCollapsed) {
                                // Task was collapsed after being expanded - need to manually re-expand
                                // The Gantt library's open-task API fails because task state is inconsistent
                                // So we'll manually call getFPConsultants and ensure proper state
                                console.log(`Task ${taskId} was previously collapsed, manually re-expanding`);
                                
                                // IMPORTANT: We preventDefault to manually control the expansion
                                event.preventDefault();
                                event.stopPropagation();
                                
                                try {
                                    // First, ensure the parent task is marked as open in the tasks array
                                    // This is critical for getFPConsultants to work properly
                                    const taskInState = tasks.find(t => t.id.toString() === taskId.toString());
                                    if (taskInState && apiRef.current) {
                                        // Update parent task to be open (this helps with DOM rendering)
                                        try {
                                            // Build complete task object with all required fields
                                            const completeTask = {
                                                id: taskInState.id,
                                                text: taskInState.text || '',
                                                start: taskInState.start || new Date(),
                                                end: taskInState.end || new Date(),
                                                duration: taskInState.duration || 1,
                                                progress: taskInState.progress || null,
                                                type: taskInState.type || "summary",
                                                lazy: true, // Keep lazy for future collapses
                                                open: true, // Must be true to show children
                                                Links: taskInState.Links || [],
                                                details: taskInState.details || ''
                                            };
                                            
                                            // Try to update task state - but don't fail if it errors
                                            try {
                                                const updateResult = apiRef.current.exec("update-task", {
                                                    id: taskId,
                                                    task: completeTask
                                                });
                                                
                                                // Suppress expected errors
                                                if (updateResult && typeof updateResult.catch === 'function') {
                                                    updateResult.catch((err) => {
                                                        // Ignore errors - we'll proceed anyway
                                                    });
                                                }
                                            } catch (updateErr) {
                                                // Ignore update errors - proceed to manual expansion
                                            }
                                        } catch (err) {
                                            // Ignore errors
                                        }
                                    }
                                    
                                    // Clear the loaded tasks ref to force reload
                                    if (loadedTasksRef.current) {
                                        loadedTasksRef.current.delete(taskId);
                                    }
                                    if (pendingRequestsRef.current) {
                                        pendingRequestsRef.current.delete(taskId);
                                    }
                                    
                                    // CRITICAL: DO NOT remove from collapsedTasksRef here!
                                    // We need to keep it in collapsedTasksRef so getFPConsultants can detect re-expansion
                                    // getFPConsultants will remove it after consultants are successfully added
                                    console.log(`Task ${taskId} kept in collapsedTasksRef for re-expansion detection`);
                                    
                                    // Manually trigger getFPConsultants after ensuring parent task is properly set up
                                    // Wait longer to ensure parent task is fully registered in Gantt library state
                                    setTimeout(() => {
                                        if (apiRef.current) {
                                            // First, verify parent task row exists and is visible
                                            const parentRow = document.querySelector(`[data-id="${taskId}"]`);
                                            if (!parentRow) {
                                                console.error(`Parent task row ${taskId} not found in DOM during re-expansion`);
                                                // If parent not found, remove from collapsedTasksRef
                                                if (window.collapsedTasksRef) {
                                                    window.collapsedTasksRef.delete(taskId);
                                                }
                                                return;
                                            }
                                            
                                            console.log(`Manually calling getFPConsultants for ${taskId} (re-expansion)`);
                                            
                                            // For re-expansion, we need to ensure the parent task is properly set up
                                            // But we do this carefully to avoid breaking the library state
                                            try {
                                                // Just verify the parent task exists in tasks array
                                                const parentTask = tasks.find(t => t.id.toString() === taskId.toString());
                                                if (!parentTask) {
                                                    console.error(`Parent task ${taskId} not found in tasks array`);
                                                    // If parent not found, remove from collapsedTasksRef
                                                    if (window.collapsedTasksRef) {
                                                        window.collapsedTasksRef.delete(taskId);
                                                    }
                                                    return;
                                                }
                                                
                                                // Don't call update-task here - it causes library crashes
                                                // The parent task should already be in the library's state
                                                
                                            } catch (setupErr) {
                                                console.warn('Could not verify parent task, proceeding anyway:', setupErr);
                                            }
                                            
                                            // Call getFPConsultants - it will handle adding consultants
                                            // We've already cleared loadedTasksRef, so it will reload
                                            // getFPConsultants will detect re-expansion via collapsedTasksRef
                                            getFPConsultants(taskId);
                                        }
                                    }, 200); // Longer delay to ensure parent task is ready
                                    
                                    // Update toggle icon to show expanded state
                                    setTimeout(() => {
                                        const taskRow = document.querySelector(`[data-id="${taskId}"]`);
                                        if (taskRow) {
                                            const toggleIcon = taskRow.querySelector('.wx-toggle-icon');
                                            if (toggleIcon) {
                                                toggleIcon.classList.add('wx-custom-expanded');
                                            }
                                        }
                                    }, 300);
                                    
                                } catch (error) {
                                    console.error('Error manually re-expanding collapsed task:', error);
                                    // Remove from collapsed ref even on error
                                    if (window.collapsedTasksRef) {
                                        window.collapsedTasksRef.delete(taskId);
                                    }
                                }
                            }
                            // If not previously collapsed (first time expand), let default behavior proceed normally
                            // The Gantt library will trigger request-data automatically
                        }
                    }, true); // Capture phase - intercept expanded, handle collapsed that were loaded before

                    // Use MutationObserver to detect when rows are added/removed by Gantt library
                    const observer = new MutationObserver(() => {
                        // Don't interfere while we're actively adding consultants
                        if (addingConsultantsRef.current) {
                            return;
                        }

                        // Update all toggle icons based on current state
                        const allToggleIcons = ganttContainer.querySelectorAll('.wx-toggle-icon');
                        allToggleIcons.forEach(icon => {
                            const row = icon.closest('[data-id]');
                            if (row) {
                                const taskId = row.getAttribute('data-id');
                                const hasChildren = tasks.some(task => task.parent === taskId);
                                const hasMessageRow = document.querySelector(`[data-message-for="${taskId}"]`);

                                // Only REMOVE the expanded class if there are no children
                                // Don't automatically ADD the class - let the expand handler do that
                                // This prevents the observer from interfering with collapse operations
                                // Note: Button tasks are now part of tasks array, so hasChildren will include them
                                if (!hasChildren && !hasMessageRow) {
                                    icon.classList.remove('wx-custom-expanded');
                                }
                                // Note: We don't add the class here anymore to prevent conflicts
                                // The class is added by the expand handler when children are loaded
                            }
                        });
                    });

                    // Observe changes in the Gantt grid body
                    const gridBody = ganttContainer.querySelector('.wx-body');
                    if (gridBody) {
                        observer.observe(gridBody, {
                            childList: true,
                            subtree: true
                        });
                    }
                }
            }, 1000);

            apiRef.current.on("select-task", ev => {
                // Task selection handler - DISABLED to avoid conflicts with single/double-click handlers
                // Edit dialog is now opened via click handlers on Firma name only

                // Don't open edit dialog if we're currently adding consultants
                if (addingConsultantsRef.current) {
                    return;
                }

                // Commented out to allow only Firma name clicks to open edit dialog
                /*
                if (ev.id) {
                    // Find the task data from the tasks array
                    const taskData = tasks.find(task => task.id.toString() === ev.id.toString());

                    if (taskData && taskData.type !== 'consultant') {
                        // Only allow editing of main FP tasks, not consultant subtasks
                        openEditDialog(taskData);
                    } else {
                    }
                }
                */
            });

            // Monitor edit-task events to set edit mode
            apiRef.current.on("edit-task", (ev) => {

                // Set edit mode when built-in editor is triggered
                if (ev.id) {
                    const taskData = tasks.find(task => task.id.toString() === ev.id.toString());
                    if (taskData && taskData.type !== 'consultant') {
                        setIsEditMode(true);
                        setEditingTaskData(taskData);
                    }
                }
            });

            // Add double-click handler for task editing
            setTimeout(() => {
                addDoubleClickHandler();
            }, 1000); // Delay to ensure DOM is ready

            // Handle sorting events
            apiRef.current.on("sort-data", (ev) => {
                if (onSort && ev.column && ev.dir) {
                    onSort(ev.column, ev.dir);
                }
            });

            // Monitor events for sidebar handling with debouncing
            let sidebarTimeout;
            apiRef.current.on("show-sidebar", (ev) => {
                try {
                    // Prevent sidebar from showing during transition
                    if (sidebarTransitionRef.current) {
                        return false;
                    }

                    // Prevent sidebar from showing if we're adding consultants
                    if (addingConsultantsRef.current) {
                        // Forcefully close it immediately
                        setTimeout(() => {
                            const sidebar = document.querySelector('.wx-sidebar');
                            if (sidebar) {
                                sidebar.style.display = 'none';
                            }
                        }, 0);
                        return false; // Try to prevent the event
                    }

                    // Check if this is a consultant task - never show sidebar for consultants
                    if (ev.id && ev.id.toString().includes('_consultant_')) {
                        setTimeout(() => {
                            const sidebar = document.querySelector('.wx-sidebar');
                            if (sidebar) {
                                sidebar.style.display = 'none';
                            }
                        }, 0);
                        return false;
                    }

                    // Immediately try to format date inputs as soon as sidebar is shown
                    setTimeout(() => formatDateInputs(), 50);

                if (!sidebarInjectedRef.current) {
                    sidebarInjectedRef.current = true;
                    clearTimeout(sidebarTimeout);
                    sidebarTimeout = setTimeout(() => {
                        // Wait longer to ensure the sidebar DOM is fully rendered
                        setTimeout(() => {
                            const sidebar = document.querySelector('.wx-sidebar');
                            if (sidebar) {
                                // FORMAT DATE INPUTS IMMEDIATELY
                                formatDateInputs();

                                const inputs = sidebar.querySelectorAll('input');
                                inputs.forEach((input, index) => {
                                });

                                // Prevent auto-submit on Enter key in textarea AND prevent form submission
                                const textarea = sidebar.querySelector('textarea');
                                if (textarea && !textarea.hasAttribute('data-enter-handler-added')) {
                                    textarea.setAttribute('data-enter-handler-added', 'true');

                                    // Multiple event listeners to catch all cases
                                    const preventSubmit = (e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.stopImmediatePropagation();
                                            return false;
                                        }
                                    };

                                    textarea.addEventListener('keypress', preventSubmit, true);
                                    textarea.addEventListener('keydown', preventSubmit, true);
                                    textarea.addEventListener('keyup', preventSubmit, true);
                                }

                                // Also prevent form submission on the entire sidebar
                                const form = sidebar.querySelector('form') || sidebar;
                                if (form && !form.hasAttribute('data-submit-prevented')) {
                                    form.setAttribute('data-submit-prevented', 'true');

                                    form.addEventListener('submit', (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.stopImmediatePropagation();
                                        return false;
                                    }, true);

                                    // Also prevent on keypress/keydown for the entire form
                                    form.addEventListener('keypress', (e) => {
                                        if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.stopImmediatePropagation();
                                            return false;
                                        }
                                    }, true);

                                    form.addEventListener('keydown', (e) => {
                                        if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.stopImmediatePropagation();
                                            return false;
                                        }
                                    }, true);
                                }
                            }

                            // Stagger the injections with more time for DOM to be ready
                            setTimeout(() => {
                                formatDateInputs(); // Format date inputs to dd.mm.yyyy
                                injectWorkWeekSelector();
                                hideFPIDField(); // Hide the FP-ID field created by wx-react-gantt
                                injectCompanySearch();
                                injectHiddenFPIDField(); // Add hidden FP-ID field programmatically
                                translateButtonLabels(); // Move initial translation here

                                // Pre-populate work week config from details field if it exists
                                setTimeout(() => {
                                    loadWorkWeekFromDetails();
                                }, 200);
                            }, 200);
                        }, 300); // Increased wait time for DOM rendering

                        // Reset flag after injection is complete
                        setTimeout(() => {
                            sidebarInjectedRef.current = false;
                        }, 2000); // Increased timeout

                        // Also inject a save button for temporary tasks and hook the existing save
                        // Try multiple times with different delays - ensure all DOM elements are ready
                        const injectTimes = [600, 1000, 1400, 1800, 2200, 3000];
                        injectTimes.forEach(delay => {
                            setTimeout(() => {
                                formatDateInputs(); // Format date inputs at each retry
                                injectMissingFPIDField();
                                injectSaveButtonForTempTask();
                                hookExistingSaveButton();
                                translateButtonLabels();

                                // Retry work week injection if it failed
                                const workWeekExists = document.querySelector('#work-week-selector-container');
                                const sidebarExists = document.querySelector('.wx-sidebar');

                                if (!workWeekExists && sidebarExists) {
                                    injectWorkWeekSelector();
                                }

                                // Ensure company search is working properly by re-injecting if needed
                                const companySearchField = document.querySelector('input[name="companySearch"]');
                                if (companySearchField && !companySearchField.hasAttribute('data-search-injected')) {
                                    injectCompanySearch();
                                }

                                // Pre-populate fields if in edit mode (try multiple times)
                                const isEditingRef = editModeRef.current.isEditing;
                                const editDataRef = editModeRef.current.taskData;

                                if ((isEditMode && editingTaskData) || (isEditingRef && editDataRef)) {
                                    prePopulateEditFields();
                                }
                            }, delay);
                        });
                    }, 200); // Initial delay
                }
                } catch (showSidebarError) {
                    console.error('Error in show-sidebar handler:', showSidebarError);
                }
            });

            // Handle when temporary tasks are added
            apiRef.current.on("add-task", (ev) => {
                // Check if this is a temporary task
                const tempTaskId = ev.id || (ev.task && ev.task.id);

                if (tempTaskId && (tempTaskId.toString().startsWith('temp_new_task_') || tempTaskId.toString().startsWith('edit_temp_task_'))) {

                    // Only show sidebar for the first occurrence to avoid duplicates
                    if (!processedTempTasksRef.current.has(tempTaskId + '_sidebar')) {
                        processedTempTasksRef.current.add(tempTaskId + '_sidebar');

                        // Show sidebar for the newly created temp task
                        setTimeout(() => {
                            try {
                                const taskData = ev.task || ev;

                                apiRef.current.exec("show-sidebar", {
                                    task: taskData
                                });

                                // Force sidebar to show by removing display: none
                                setTimeout(() => {
                                    const sidebar = document.querySelector('.wx-sidebar');
                                    if (sidebar) {
                                        // Force sidebar to show if it's hidden
                                        if (sidebar.style.display === 'none' || sidebar.offsetWidth === 0) {
                                            sidebar.style.display = '';
                                            sidebar.style.visibility = '';
                                        }
                                    }
                                }, 50);

                            // Also hide the task row
                            setTimeout(() => {
                                hideTemporaryTaskRow(tempTaskId);
                            }, 200);

                            } catch (error) {
                                console.error('Error showing sidebar for temp task:', error);
                            }
                        }, 100);
                    }
                }
            });

            // Handle task creation/update for new MA-Beratungslauf
            apiRef.current.on("update-task", (ev) => {

                // Check if this is a message task (no consultants) - ignore these completely
                const isMessageTask = ev.id && ev.id.toString().startsWith('no_consultants_');
                if (isMessageTask) {
                    return;
                }

                // Check if this is a temporary task (starts with temp_new_task_ or edit_temp_task_)
                const isTempTask = ev.id && (ev.id.toString().startsWith('temp_new_task_') || ev.id.toString().startsWith('edit_temp_task_'));

                if (isTempTask && ev.task) {
                    // COMPLETELY DISABLE automatic processing of temporary tasks
                    // Only process when explicitly triggered from handleManualSave
                    return;

                    // For edit temp tasks, don't auto-process them - let the user interact with the sidebar
                    if (ev.id.toString().startsWith('edit_temp_task_')) {
                        return;
                    }

                    // Mark this task as processed
                    processedTempTasksRef.current.add(ev.id);


                    // Get FP_ID from task data or from DOM (fallback)
                    const fpIdFromTask = ev.task.FP_ID;
                    const fpIdFromDOM = document.querySelector('input[name="FP_ID"]')?.value ||
                                      document.querySelector('input[data-key="FP_ID"]')?.value ||
                                      document.querySelector('.wx-sidebar input[type="number"]')?.value;

                    const fpIdValue = fpIdFromTask || fpIdFromDOM;

                    // Check if this update is just from typing (inProgress = true means user is still editing)
                    if (ev.inProgress) {
                        processedTempTasksRef.current.delete(ev.id); // Allow retry when user finishes
                        return;
                    }

                    // Also check if we have required data before proceeding
                    const hasRequiredData = fpIdValue && fpIdValue.toString().trim() !== '';

                    if (!hasRequiredData) {

                        // Try to get ALL form field values from DOM for debugging
                        const allInputs = document.querySelectorAll('.wx-sidebar input, .wx-sidebar textarea, .wx-sidebar select');
                        allInputs.forEach((input, index) => {
                            console.log({
                                name: input.name,
                                value: input.value,
                                type: input.type,
                                placeholder: input.placeholder,
                                id: input.id
                            });
                        });

                        // Don't delete the task yet, let user continue editing
                        processedTempTasksRef.current.delete(ev.id); // Allow retry
                        return;
                    }

                    // Remove the temporary task from the Gantt first
                    setTimeout(() => {
                        try {
                            // Check if task still exists before trying to delete
                            const taskExists = document.querySelector(`[data-task-id="${ev.id}"]`);
                            if (taskExists) {
                                apiRef.current.exec("delete-task", { id: ev.id });
                            }
                        } catch (deleteError) {
                        }

                        // Check if we're in edit mode or create mode (check both state and ref)
                        const isEditingRef = editModeRef.current.isEditing;
                        const editDataRef = editModeRef.current.taskData;

                        if ((isEditMode && editingTaskData) || (isEditingRef && editDataRef)) {
                            handleEditConsCompanyUpdate(ev.task, fpIdValue);
                        } else {
                            handleNewConsCompanyCreation(ev.task, fpIdValue);
                        }

                        // Reset edit mode if we were editing (reset both state and ref)
                        if (isEditMode || editModeRef.current.isEditing) {
                            setIsEditMode(false);
                            setEditingTaskData(null);
                            editModeRef.current = { isEditing: false, taskData: null };
                        }

                        // Clean up the processed task tracking after a delay
                        setTimeout(() => {
                            processedTempTasksRef.current.delete(ev.id);
                        }, 5000);
                    }, 100);
                } else {
                    // Check if this is a new task creation (no existing id in tasks array)
                    const isNewTask = !tasks.find(task => task.id === ev.id);

                    // Only call creation handler for top-level FP tasks, not consultant subtasks
                    const isConsultantTask = ev.task?.type === 'consultant' || ev.task?.parent;

                    if (isNewTask && ev.task && !isTempTask && !isConsultantTask) {
                        handleNewConsCompanyCreation(ev.task);
                    }
                }
            });

            // Clean up event listeners
            return () => {
                if (apiRef.current) {
                    try {
                        apiRef.current.detach("request-data");
                        apiRef.current.detach("select-task");
                        apiRef.current.detach("edit-task");
                        apiRef.current.detach("sort-data");
                        apiRef.current.detach("show-sidebar");
                        apiRef.current.detach("add-task");
                        apiRef.current.detach("update-task");
                        eventsAttachedRef.current = false;
                        sidebarInjectedRef.current = false;
                        processedTempTasksRef.current.clear();
                        clearTimeout(sidebarTimeout);
                    } catch (e) {
                        // Event cleanup failed
                    }
                }
            };
        }
    }, [apiRef.current]);

    // Handle button click to create new MA-Beratungslauf with proper task structure
    useEffect(() => {
        const attachButtonHandler = () => {
            const addTaskButton = document.querySelector('[data-id="add-task"] button');

            if (addTaskButton && !addTaskButton.hasAttribute('data-custom-handler')) {
                addTaskButton.setAttribute('data-custom-handler', 'true');

                addTaskButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Set transition flag to prevent race conditions
                    sidebarTransitionRef.current = true;

                    // First, clean up any existing state
                    try {
                        // Hide any existing temporary tasks
                        const existingTempTasks = document.querySelectorAll('[data-id^="temp_new_task_"], [data-id^="edit_temp_task_"]');
                        existingTempTasks.forEach(tempTask => {
                            tempTask.style.display = 'none';
                        });

                        // Reset edit mode state
                        setIsEditMode(false);
                        setEditingTaskData(null);
                        editModeRef.current = { isEditing: false, taskData: null };

                        // Reset sidebar injection flag
                        sidebarInjectedRef.current = false;

                        // Clear processed tasks tracking
                        processedTempTasksRef.current.clear();

                        // Close any existing sidebar
                        if (apiRef.current) {
                            try {
                                apiRef.current.exec("close-sidebar");
                            } catch (closeErr) {
                                // Ignore close errors
                            }
                        }
                    } catch (cleanupErr) {
                        console.error('Cleanup error:', cleanupErr);
                    }

                    setTimeout(() => {
                        // Clear transition flag
                        sidebarTransitionRef.current = false;

                        if (apiRef.current) {
                            try {
                                // Create a temporary task that will be hidden
                                const tempTaskId = `temp_new_task_${Date.now()}`;
                                const tempTask = {
                                    id: tempTaskId,
                                    text: "Neue MA-Beratungslauf",
                                    start: new Date(),
                                    end: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)), // 30 days from now
                                    duration: 30,
                                    progress: 0,
                                    type: "task",
                                    companySearch: "",
                                    FP_ID: "",
                                    details: "",
                                    hidden: true // Mark as hidden
                                };

                                // Add the task to enable sidebar, but hide it immediately
                                apiRef.current.exec("add-task", {
                                    task: tempTask
                                });

                                // Hide the task row immediately after adding
                                setTimeout(() => {
                                    hideTemporaryTaskRow(tempTaskId);
                                }, 100);

                            } catch (error) {
                                console.error('Error creating hidden temp task:', error);
                                sidebarTransitionRef.current = false;
                            }
                        } else {
                            sidebarTransitionRef.current = false;
                        }
                    }, 300); // Increased delay to allow cleanup to complete
                });
            }
        };

        // Set up a MutationObserver to watch for button changes
        const observer = new MutationObserver(() => {
            attachButtonHandler();
        });

        // Observe the toolbar for changes
        const toolbar = document.querySelector('.wx-toolbar') || document.querySelector('.wx-gantt');
        if (toolbar) {
            observer.observe(toolbar, {
                childList: true,
                subtree: true
            });
        }

        // Initial attachment
        const timer = setTimeout(attachButtonHandler, 1000);

        // Re-check periodically in case button is recreated
        const interval = setInterval(attachButtonHandler, 2000);

        return () => {
            clearTimeout(timer);
            clearInterval(interval);
            observer.disconnect();
        };
    }, []); // Empty dependency array - run once and use observer for updates

    // Detect when Gantt is fully rendered
    useEffect(() => {
        if (!loading && onGanttReady && tasks.length > 0) {
            const checkGanttReady = () => {
                // Check for specific Gantt elements that indicate full rendering
                const ganttContainer = document.querySelector('.wx-gantt');
                const ganttHeaders = document.querySelectorAll('.wx-grid-header .wx-cell');
                const ganttRows = document.querySelectorAll('.wx-grid-data .wx-row');

                // Consider ready when we have container, headers, and either rows or empty state
                if (ganttContainer && ganttHeaders.length > 0) {
                    setIsGanttReady(true);
                    onGanttReady();
                    return true;
                }
                return false;
            };

            // Try multiple times with increasing delays
            const timers = [
                setTimeout(() => checkGanttReady(), 500),
                setTimeout(() => checkGanttReady(), 1000),
                setTimeout(() => checkGanttReady(), 1500),
                setTimeout(() => {
                    setIsGanttReady(true);
                    onGanttReady();
                }, 2500)
            ];

            return () => {
                timers.forEach(timer => clearTimeout(timer));
            };
        } else if (!loading && onGanttReady && tasks.length === 0) {
            // If no tasks, wait a bit for empty state to render
            const timer = setTimeout(() => {
                setIsGanttReady(true);
                onGanttReady();
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [loading, tasks.length, onGanttReady]);

    // Re-apply view settings when Gantt becomes ready
    useEffect(() => {
        if (isGanttReady) {
            setTimeout(() => {
                // Re-calculate and apply view settings
                const settings = calculateViewSettings(currentViewMode, currentViewDate);
                if (settings) {
                    setCellWidth(settings.cellWidth);
                    setViewPeriodText(settings.periodText);
                }

                // Re-hide columns
                hideColumnsOutsideRange();
            }, 500);
        }
    }, [isGanttReady]);

    const handleNewConsCompanyCreation = async (taskData, providedFpId = null) => {
        // Declare variables outside try block so they're accessible in catch
        let fpIdValue = null;
        let finalFpId = null;
        let companyName = '';

        try {

            // Get FP_ID from parameter or fallback to finding it in DOM
            fpIdValue = providedFpId;
            if (!fpIdValue) {
                const fpIdField = document.querySelector('input[name="FP_ID"]') ||
                                 document.querySelector('input[data-key="FP_ID"]') ||
                                 document.querySelector('.wx-sidebar input[type="number"]');
                fpIdValue = fpIdField ? fpIdField.value : taskData.FP_ID;
            }

            // Get company name from the company search field for better task naming
            const companySearchField = document.querySelector('input[name="companySearch"]') ||
                                      document.querySelector('input[data-key="companySearch"]') ||
                                      document.querySelector('.wx-sidebar input[placeholder*="Firmennamen"]');
            companyName = companySearchField ? companySearchField.value : '';


            // Validate required fields
            if (!fpIdValue || fpIdValue === '') {
                toast.error('FP-ID ist erforderlich');
                return false;
            }

            if (!taskData.start || !taskData.end) {
                toast.error('Start- und Enddatum sind erforderlich');
                return false;
            }

            // Prepare data for API call
            const formData = new FormData();

            // Use the provided FP_ID (already numeric) or parse the one from form
            finalFpId = typeof providedFpId === 'number' ? providedFpId : parseInt(fpIdValue, 10);

            formData.append('FP_ID', finalFpId);
            formData.append('fromDate', taskData.start.toISOString().split('T')[0]); // YYYY-MM-DD format
            formData.append('toDate', taskData.end.toISOString().split('T')[0]); // YYYY-MM-DD format
            formData.append('planer', user.email); // Use current user's email as planner


            setLoading(true);

            // Call the API
            const response = await Api().post('sp_putNewConsCompany', formData);

            setLoading(false);

            // Check response structure
            if (response.data && response.data.success) {
                // Show success message
                toast.success('MA-Beratungslauf erfolgreich erstellt');

                // Refresh the data to show the new entry
                // This will trigger a reload of the main data
                if (onSort) {
                    // Trigger a refresh by calling the sort function with current parameters
                    onSort(currentSort?.column || '7', currentSort?.method || 'asc');
                }

                // Return true to indicate success
                return true;
            } else {
                // Handle unexpected response format
                toast.error('Unerwartete Antwort vom Server');
                return false;
            }

        } catch (error) {
            setLoading(false);
            console.error('Error creating new MA-Beratungslauf:', error);

            // Check for specific error types
            if (error.response && error.response.data) {
                const errorData = error.response.data;

                // Check for duplicate FP error
                if (errorData.error === 'FP_ALREADY_EXISTS') {
                    // Show modal instead of toast
                    setDuplicateFpId(finalFpId);
                    setDuplicateFpName(companyName);
                    setFpAlreadyExistsModalOpen(true);
                    // Return false to indicate failure (don't close sidebar or refresh)
                    return false;
                }
            }

            // For all other errors, re-throw to be handled by caller
            throw error;
        }
    };

    const handleEditConsCompanyUpdate = async (taskData, providedFpId = null) => {
        try {

            // Get FP_ID from parameter or fallback to finding it in DOM
            let fpIdValue = providedFpId;
            if (!fpIdValue) {
                const fpIdField = document.querySelector('input[name="FP_ID"]') ||
                                 document.querySelector('input[data-key="FP_ID"]') ||
                                 document.querySelector('.wx-sidebar input[type="number"]');
                fpIdValue = fpIdField ? fpIdField.value : taskData.FP_ID;
            }

            // Get company name from the company search field
            const companySearchField = document.querySelector('input[name="companySearch"]') ||
                                      document.querySelector('input[data-key="companySearch"]') ||
                                      document.querySelector('.wx-sidebar input[placeholder*="Firmennamen"]');
            const companyName = companySearchField ? companySearchField.value : '';

            // Get details/additional information
            const detailsField = document.querySelector('.wx-sidebar textarea') ||
                                document.querySelector('.wx-sidebar input[data-key="details"]');
            const details = detailsField ? detailsField.value : '';


            // Validate required fields
            if (!fpIdValue || fpIdValue === '') {
                toast.error('FP-ID ist erforderlich');
                return false;
            }

            if (!taskData.start || !taskData.end) {
                toast.error('Start- und Enddatum sind erforderlich');
                return false;
            }

            // Prepare data for API call
            const formData = new FormData();

            // Use the provided FP_ID (already numeric) or parse the one from form
            const finalFpId = typeof providedFpId === 'number' ? providedFpId : parseInt(fpIdValue, 10);

            formData.append('FP_ID', finalFpId);
            formData.append('fromDate', taskData.start.toISOString().split('T')[0]); // YYYY-MM-DD format
            formData.append('toDate', taskData.end.toISOString().split('T')[0]); // YYYY-MM-DD format
            formData.append('planer', user.email); // Use current user's email as planner
            formData.append('details', details); // Additional details
            formData.append('companyName', companyName); // Company name


            setLoading(true);

            // Call the API for updating
            const response = await Api().post('sp_changeConsCompaniesDetails', formData);

            setLoading(false);

            // Show success message
            toast.success('MA-Beratungslauf erfolgreich aktualisiert');

            // Refresh the data to show the updated entry
            if (onSort) {
                // Trigger a refresh by calling the sort function with current parameters
                onSort(currentSort?.column || '7', currentSort?.method || 'asc');
            }

            // Return true to indicate success
            return true;

        } catch (error) {
            setLoading(false);
            console.error('Error updating MA-Beratungslauf:', error);
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(`Fehler: ${error.response.data.message}`);
            } else {
                toast.error('Fehler beim Aktualisieren des MA-Beratungslaufs');
            }
            // Return false to indicate failure
            return false;
        }
    };

    const getFPConsultants = (id) => {
        // Prevent multiple simultaneous requests for the same task (synchronous check)
        if (pendingRequestsRef.current.has(id)) {
            return;
        }

        // Check if task already has consultant subtasks - if so, don't reload
        // IMPORTANT: Check both tasks array AND DOM to handle collapsed/expanded state
        const hasConsultantsInTasksArray = tasks.some(task =>
            task.parent === id && task.type === "consultant"
        );
        
        // Also check if consultant rows exist in DOM (they might have been deleted during collapse)
        const ganttContainer = document.querySelector('.wx-gantt');
        const gridBody = ganttContainer ? ganttContainer.querySelector('.wx-body') : null;
        const hasConsultantsInDOM = gridBody ? 
            Array.from(gridBody.querySelectorAll(`[data-id^="${id}_consultant"]`)).length > 0 : false;
        
        // Only skip reload if consultants exist in BOTH tasks array AND DOM
        // If they exist in tasks array but not DOM, they were deleted during collapse - reload them
        if (hasConsultantsInTasksArray && hasConsultantsInDOM) {
            return;
        }

        // Check if task is already loaded
        if (loadedTasksRef.current.has(id)) {
            return;
        }

        loadedTasksRef.current.add(id);
        pendingRequestsRef.current.add(id);

        const formData = new FormData();
        formData.append('portal', portal);
        formData.append('fpId', id);

        // Use local loading state instead of global to prevent page re-renders
        Api().post('sp_getFPConsultants', formData).then(res => {

            if (apiRef.current) {
                // Get parent task to use its dates as reference
                const parentTask = tasks.find(task => task.id === id);
                const parentStart = parentTask ? new Date(parentTask.start) : new Date();
                const parentEnd = parentTask ? new Date(parentTask.end) : new Date(Date.now() + (30 * 24 * 60 * 60 * 1000));

                let consultantTasks = [];

                // Check if we have valid data
                if (res.data && Array.isArray(res.data) && res.data.length > 0) {

                    // Group consultant data by partnernr to create date ranges
                    const consultantMap = new Map();

                    res.data.forEach((entry, idx) => {
                        const key = entry.partnernr;
                        if (!key) {
                            console.warn(`Entry ${idx} has no partnernr, skipping:`, entry);
                            return;
                        }
                        if (!consultantMap.has(key)) {
                            consultantMap.set(key, {
                                partnernr: entry.partnernr,
                                fullname: entry.fullname || entry.name || 'Unbekannt',
                                dates: []
                            });
                        }
                        if (entry.datum) {
                            consultantMap.get(key).dates.push(entry.datum);
                        }
                    });

                    consultantMap.forEach((value, key) => {
                    });

                    // Create consultant subtasks from grouped data
                    consultantTasks = Array.from(consultantMap.values()).map((consultant, index) => {

                        // Validate consultant has dates
                        if (!consultant.dates || consultant.dates.length === 0) {
                            console.warn(`Consultant ${consultant.fullname} has no dates, skipping`);
                            return null;
                        }

                        // Sort dates to find start and end
                        const sortedDates = consultant.dates.sort((a, b) => new Date(a) - new Date(b));
                        const consultantStart = new Date(sortedDates[0]);
                        const consultantEnd = new Date(sortedDates[sortedDates.length - 1]);


                        // Add one day to end date to make it inclusive
                        consultantEnd.setDate(consultantEnd.getDate() + 1);

                        const task = {
                            id: `${id}_consultant_${consultant.partnernr}`,
                            text: consultant.fullname,
                            start: consultantStart,
                            end: consultantEnd,
                            duration: sortedDates.length,
                            progress: 0,
                            type: "consultant",
                            parent: id,
                            lazy: false,
                            open: false,
                            Links: [],
                            details: `${consultant.fullname} (${consultant.partnernr}) - ${sortedDates.length} Tage`
                        };

                        return task;
                    }).filter(task => task !== null);
                } else {
                    // No consultants found - show message with button (DOM-based, not a Gantt task)
                    showMessageUnderFP(id, 'Keine Berater für dieses Projekt verfügbar');
                    return; // Don't add any subtasks
                }

                // Verify we have consultant tasks to add
                if (consultantTasks.length === 0) {
                    console.error('✗ No consultant tasks were created despite having data!');
                    showMessageUnderFP(id, 'Fehler beim Verarbeiten der Berater-Daten');
                    return;
                }

                // Add each consultant as a subtask

                // Set flag to prevent edit dialog from opening during consultant addition
                addingConsultantsRef.current = true;

                // Aggressively hide any sidebar that appears during consultant addition
                const observer = new MutationObserver((mutations) => {
                    if (addingConsultantsRef.current) {
                        const sidebar = document.querySelector('.wx-sidebar');
                        if (sidebar && sidebar.style.display !== 'none') {
                            sidebar.style.display = 'none';
                        }
                    }
                });

                // Start observing the document for sidebar creation
                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });

                // Store observer reference to disconnect later
                window.consultantAddObserver = observer;

                // CRITICAL: Ensure parent task exists in DOM before adding children
                // Note: We don't call update-task here to avoid breaking normal expansion
                // The parent task should already be registered from the initial task loading
                try {
                    // Check if parent task row exists in DOM
                    const parentTaskRow = document.querySelector(`[data-id="${id}"]`);
                    if (!parentTaskRow) {
                        console.error(`✗ Parent task ${id} row not found in DOM, cannot add consultants`);
                        pendingRequestsRef.current.delete(id);
                        loadedTasksRef.current.delete(id);
                        return;
                    }
                    
                    // Verify apiRef is still valid before adding consultants
                    if (!apiRef.current) {
                        console.error(`✗ apiRef is null, cannot add consultants`);
                        pendingRequestsRef.current.delete(id);
                        loadedTasksRef.current.delete(id);
                        return;
                    }
                    
                    // Add consultants sequentially with small delays
                    // IMPORTANT: Check if this is a re-expansion after collapse
                    // If so, we need to ensure parent is opened first, or use a different add method
                    const isReExpansion = window.collapsedTasksRef && window.collapsedTasksRef.has(id);
                    
                    console.log(`getFPConsultants for task ${id}: isReExpansion = ${isReExpansion}`);
                    
                    // For re-expansion, first try to open the parent task
                    if (isReExpansion && apiRef.current) {
                        try {
                            apiRef.current.exec("open-task", { id: id }).catch(() => {
                                // Ignore errors - proceed anyway
                            });
                        } catch (openErr) {
                            // Ignore errors
                        }
                    }
                    
                    // Add consultants - use parent property directly instead of target mode for re-expansion
                    // Track successful additions to remove from collapsedTasksRef when done
                    let addedCount = 0;
                    
                    consultantTasks.forEach((consultantTask, index) => {
                        setTimeout(() => {
                            try {
                                // Verify apiRef is still valid before each add
                                if (!apiRef.current) {
                                    console.error(`✗ apiRef became null while adding consultant ${index + 1}`);
                                    return;
                                }
                                
                                // For re-expansion, try adding without target mode first
                                // The parent property in the task object should be enough
                                let addResult;
                                
                                if (isReExpansion) {
                                    // Try adding with just the task object (parent property is already set)
                                    // This works even if parent isn't fully recognized in library state
                                    try {
                                        addResult = apiRef.current.exec("add-task", {
                                            task: consultantTask
                                        });
                                    } catch (altErr) {
                                        // If that fails, try with target mode as fallback
                                        addResult = apiRef.current.exec("add-task", {
                                            task: consultantTask,
                                            target: id,
                                            mode: "child"
                                        });
                                    }
                                } else {
                                    // Normal expansion - use target mode
                                    addResult = apiRef.current.exec("add-task", {
                                        task: consultantTask,
                                        target: id,
                                        mode: "child"
                                    });
                                }
                                
                                // Handle promise if exec returns one
                                if (addResult && typeof addResult.then === 'function') {
                                    addResult
                                        .then(() => {
                                            console.log(`✓ Added consultant ${index + 1}/${consultantTasks.length}: ${consultantTask.text}`);
                                            addedCount++;
                                            
                                            // If all consultants added and this is re-expansion, remove from collapsedTasksRef
                                            if (isReExpansion && addedCount === consultantTasks.length) {
                                                if (window.collapsedTasksRef) {
                                                    window.collapsedTasksRef.delete(id);
                                                }
                                            }
                                        })
                                        .catch((addErr) => {
                                            console.error(`✗ Error adding consultant ${index + 1} (${consultantTask.id}):`, addErr);
                                            
                                            // If re-expansion and first method failed, try with target mode as fallback
                                            if (isReExpansion) {
                                                try {
                                                    const fallbackResult = apiRef.current.exec("add-task", {
                                                        task: consultantTask,
                                                        target: id,
                                                        mode: "child"
                                                    });
                                                    if (fallbackResult && typeof fallbackResult.then === 'function') {
                                                        fallbackResult
                                                            .then(() => {
                                                                addedCount++;
                                                                if (addedCount === consultantTasks.length) {
                                                                    if (window.collapsedTasksRef) {
                                                                        window.collapsedTasksRef.delete(id);
                                                                    }
                                                                }
                                                            })
                                                            .catch(() => {
                                                                // Fallback also failed
                                                            });
                                                    } else {
                                                        addedCount++;
                                                        if (addedCount === consultantTasks.length) {
                                                            if (window.collapsedTasksRef) {
                                                                window.collapsedTasksRef.delete(id);
                                                            }
                                                        }
                                                    }
                                                } catch (fallbackErr) {
                                                    // Fallback also failed
                                                }
                                            }
                                        });
                                } else {
                                    console.log(`✓ Added consultant ${index + 1}/${consultantTasks.length} (sync): ${consultantTask.text}`);
                                    addedCount++;
                                    
                                    // If all consultants added and this is re-expansion, remove from collapsedTasksRef
                                    if (isReExpansion && addedCount === consultantTasks.length) {
                                        if (window.collapsedTasksRef) {
                                            window.collapsedTasksRef.delete(id);
                                        }
                                    }
                                }
                            } catch (error) {
                                console.error(`✗ Error adding consultant ${index + 1} (${consultantTask.id}):`, error);
                            }
                        }, index * 50); // Reduced delay to 50ms - parent should already be ready
                    });
                    
                } catch (parentErr) {
                    console.error(`✗ Error before adding consultants:`, parentErr);
                    pendingRequestsRef.current.delete(id);
                    loadedTasksRef.current.delete(id);
                    return;
                }


                // Explicitly open/expand the parent task to show children
                // CRITICAL: Must set open: true on parent task for children to render
                try {
                    if (apiRef.current) {
                        // First, update the parent task to set open: true
                        // This is required for the Gantt library to render child rows
                        apiRef.current.exec("update-task", {
                            id: id,
                            task: { open: true }
                        });

                        // Also call open-task for good measure
                        apiRef.current.exec("open-task", { id: id }).catch(() => {
                            // Ignore errors - parent might already be open
                        });
                    }

                    // Add expanded class to toggle icon to show [-] icon
                    // Wait longer to ensure all tasks (including button task) are added first
                    setTimeout(() => {
                        const taskRow = document.querySelector(`[data-id="${id}"]`);
                        if (taskRow) {
                            const toggleIcon = taskRow.querySelector('.wx-toggle-icon');
                            if (toggleIcon) {
                                toggleIcon.classList.add('wx-custom-expanded');
                                console.log(`✓ Added wx-custom-expanded class to toggle icon for ${id}`);
                            }
                        }
                    }, 1000); // Increased delay to ensure button task is added
                } catch (e) {
                    console.error('Error opening parent task:', e);
                }

                // CRITICAL FIX: Force React re-render to recalculate gantt bar positions
                // After adding consultant subtasks via apiRef.exec(), the Gantt library's internal
                // state is updated but React doesn't detect the change. This causes the left tree
                // to expand correctly, but the gantt bars on the right remain at their old Y-positions.
                // We force a re-render by making an imperceptible change to cellHeight prop.

                // Show loading overlay during re-render to hide misalignment
                const ganttContainer = document.querySelector('.wx-gantt');
                let loadingOverlay = null;
                if (ganttContainer) {
                    loadingOverlay = document.createElement('div');
                    loadingOverlay.style.cssText = `
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(255, 255, 255, 0.8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 9999;
                        pointer-events: none;
                    `;
                    loadingOverlay.innerHTML = `
                        <div style="
                            width: 24px;
                            height: 24px;
                            border: 3px solid #f3f3f3;
                            border-top: 3px solid #3A46A9;
                            border-radius: 50%;
                            animation: spin 0.8s linear infinite;
                        "></div>
                        <style>
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        </style>
                    `;
                    ganttContainer.style.position = 'relative';
                    ganttContainer.appendChild(loadingOverlay);
                }

                // CRITICAL: Force Gantt to fully recalculate bar positions
                // The wx-react-gantt library doesn't automatically recalculate Y positions
                // when the DOM tree changes, so we use multiple techniques to force it
                setTimeout(() => {
                    // Method 1: Dispatch window resize event to trigger Gantt's resize handler
                    window.dispatchEvent(new Event('resize'));

                    setTimeout(() => {
                        // Method 2: Toggle cellHeight (Step 1)
                        setCellHeight(prevHeight => prevHeight + 5);

                        setTimeout(() => {
                            // Method 3: Another resize event
                            window.dispatchEvent(new Event('resize'));

                            setTimeout(() => {
                                // Method 4: Revert cellHeight (Step 2)
                                setCellHeight(prevHeight => prevHeight - 5);

                                setTimeout(() => {
                                    // Method 5: Final resize event
                                    window.dispatchEvent(new Event('resize'));

                                    // Remove loading overlay
                                    setTimeout(() => {
                                        if (loadingOverlay && loadingOverlay.parentNode) {
                                            loadingOverlay.parentNode.removeChild(loadingOverlay);
                                        }

                                        // Verify consultant rows in DOM
                                        const consultantRows = document.querySelectorAll(`[data-id^="${id}_consultant"]`);
                                        console.log(`✓ Found ${consultantRows.length} consultant rows in DOM for task ${id}`);
                                    }, 50);
                                }, 200);
                            }, 200);
                        }, 200);
                    }, 200);
                }, 800); // Wait longer for DOM to fully settle

                // Deselect any selected tasks and close sidebar
                setTimeout(() => {
                    try {
                        if (apiRef.current) {
                            // Unselect any selected task
                            apiRef.current.exec("unselect-task");

                            // Close the sidebar if it opened
                            apiRef.current.exec("close-sidebar");

                            // Force hide the sidebar with CSS if it's still visible
                            setTimeout(() => {
                                const sidebar = document.querySelector('.wx-sidebar');
                                if (sidebar) {
                                    sidebar.style.display = 'none';
                                }
                            }, 50);

                            // Reset the flag after a delay to ensure all events have processed
                            setTimeout(() => {
                                addingConsultantsRef.current = false;

                                // Disconnect the mutation observer
                                if (window.consultantAddObserver) {
                                    window.consultantAddObserver.disconnect();
                                    window.consultantAddObserver = null;
                                }
                            }, 300);
                        }
                    } catch (e) {
                        // Reset flag even if there was an error
                        addingConsultantsRef.current = false;

                        // Disconnect observer even on error
                        if (window.consultantAddObserver) {
                            window.consultantAddObserver.disconnect();
                            window.consultantAddObserver = null;
                        }
                    }
                }, 200);

                // Clear any existing message for this FP since we now have consultants
                clearMessageForFP(id);
                clearNoConsultantsTask(id);

                // Add permanent "Berater hinzufügen" button row after consultants
                // Wait longer to ensure consultant rows are fully rendered in DOM
                setTimeout(() => {
                    showAddConsultantButton(id);
                }, 300);

                // Shift timeline elements and increase container heights for consultant rows
                setTimeout(() => {
                    const heightToAdd = consultantTasks.length * 40;

                    // Shift timeline elements below this firma
                    const ganttContainer = document.querySelector('.wx-gantt');
                    if (ganttContainer) {
                        const gridBody = ganttContainer.querySelector('.wx-body');
                        const wxArea = ganttContainer.querySelector('.wx-area');

                        if (gridBody && wxArea) {
                            const allGridRows = Array.from(gridBody.querySelectorAll('[data-id]'));
                            const expandedRowIndex = allGridRows.findIndex(row => row.getAttribute('data-id') === id);
                            const timelineElements = wxArea.querySelectorAll('[data-task-id], .wx-bar, .wx-link');

                            timelineElements.forEach((element) => {
                                const elementTaskId = element.getAttribute('data-task-id') || element.getAttribute('data-id');
                                if (elementTaskId && elementTaskId !== id) { // Don't shift the expanded firma's own timeline
                                    const elementRowIndex = allGridRows.findIndex(row => row.getAttribute('data-id') === elementTaskId);
                                    // Only shift elements STRICTLY BELOW the expanded firma
                                    if (elementRowIndex > expandedRowIndex && elementRowIndex !== -1) {
                                        const currentTop = parseInt(element.style.top) || 0;
                                        const newTop = currentTop + heightToAdd;
                                        element.style.top = `${newTop}px`;
                                        element.setAttribute('data-shifted-by', id);
                                        element.setAttribute('data-shift-amount', heightToAdd.toString());
                                    }
                                }
                            });
                        }
                    }

                    updateGanttContainerHeights(heightToAdd);
                }, 150);

                // Update the berater_count for the parent task
                updateBeraterCount(id, consultantTasks.length);
            }

            // Clear pending request (synchronous)
            pendingRequestsRef.current.delete(id);
        }).catch((error) => {
            toast.error('Fehler beim Laden der Berater!');
            // Remove from loaded tasks so it can be retried (synchronous)
            loadedTasksRef.current.delete(id);
            // Clear pending request (synchronous)
            pendingRequestsRef.current.delete(id);
            // Reset the adding consultants flag in case of error
            addingConsultantsRef.current = false;

            // Disconnect observer in error case
            if (window.consultantAddObserver) {
                window.consultantAddObserver.disconnect();
                window.consultantAddObserver = null;
            }
        });
    };

    // Function to add a "no consultants" task that properly shifts the timeline
    const addNoConsultantsTask = (parentTaskId, parentStart, parentEnd) => {
        try {

            const messageTaskId = `no_consultants_${parentTaskId}`;

            // Check if the message task already exists
            const existingTask = tasks.find(task => task.id === messageTaskId);
            if (existingTask) {
                return;
            }

            const noConsultantsTask = {
                id: messageTaskId,
                text: "Keine Berater verfügbar",
                start: new Date(parentStart),
                end: new Date(parentEnd),
                duration: Math.ceil((parentEnd.getTime() - parentStart.getTime()) / (24 * 60 * 60 * 1000)),
                progress: 0,
                type: "message", // Custom type for message tasks
                parent: parentTaskId,
                berater_name: "",
                berater_count: 0,
                links: [],
                hasAddButton: true, // Flag to show add consultant button
                fpId: parentTaskId,
                lazy: false, // Not expandable
                readonly: true // Cannot be edited
            };

            // Add the message task as a child
            apiRef.current.exec("add-task", {
                task: noConsultantsTask,
                target: parentTaskId,
                mode: "child"
            });


            // Add click handler for the "add consultant" functionality after DOM is ready
            setTimeout(() => {
                addClickHandlerToMessageTask(noConsultantsTask.id, parentTaskId);
            }, 500);

        } catch (error) {
            console.error('Error adding no consultants task:', error);
            // Fallback to toast notification if Gantt task creation fails
            toast.info('Keine Berater für dieses Projekt verfügbar');
        }
    };

    // Function to add click handler to message task for opening consultant dialog
    const addClickHandlerToMessageTask = (messageTaskId, parentTaskId) => {
        try {

            // Wait longer for DOM to be ready
            const setupHandler = () => {
                const messageTaskRow = document.querySelector(`[data-id="${messageTaskId}"]`);

                if (messageTaskRow) {
                    // Style the message task row to look like a clickable button
                    messageTaskRow.style.cursor = 'pointer';
                    messageTaskRow.style.backgroundColor = '#f9fafb';
                    messageTaskRow.style.borderLeft = '4px solid #3b82f6';

                    // Remove any existing click handlers
                    const existingHandlers = messageTaskRow.querySelectorAll('[data-click-handler]');
                    existingHandlers.forEach(handler => handler.remove());

                    // Add a button element for the "Berater hinzufügen" action
                    const addButton = document.createElement('button');
                    addButton.setAttribute('data-click-handler', 'true');
                    addButton.textContent = 'Berater hinzufügen';
                    addButton.style.cssText = `
                        background-color: #3b82f6;
                        color: white;
                        border: none;
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        cursor: pointer;
                        margin-left: 8px;
                    `;

                    // Add click handler to the button
                    addButton.addEventListener('click', (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        showAddConsultantDialog(parentTaskId);

                        // Remove the message task after opening dialog
                        try {
                            apiRef.current.exec("delete-task", { id: messageTaskId });
                        } catch (error) {
                            console.error('Error removing message task:', error);
                        }
                    });

                    // Add the button to the message row text cell
                    const textCell = messageTaskRow.querySelector('.wx-cell-content') || messageTaskRow.querySelector('.wx-cell');
                    if (textCell) {
                        textCell.appendChild(addButton);
                    }

                    // Add hover effects to the row
                    messageTaskRow.addEventListener('mouseenter', () => {
                        messageTaskRow.style.backgroundColor = '#f3f4f6';
                    });

                    messageTaskRow.addEventListener('mouseleave', () => {
                        messageTaskRow.style.backgroundColor = '#f9fafb';
                    });

                    // Prevent the row from being expandable
                    const toggleIcon = messageTaskRow.querySelector('.wx-toggle-icon');
                    if (toggleIcon) {
                        toggleIcon.style.display = 'none';
                    }

                } else {
                }
            };

            // Try multiple times with increasing delays
            setTimeout(setupHandler, 100);
            setTimeout(setupHandler, 500);
            setTimeout(setupHandler, 1000);
            setTimeout(setupHandler, 1500);

        } catch (error) {
            console.error('Error adding click handler to message task:', error);
        }
    };

    // Function to update berater count for a parent task
    const updateBeraterCount = (parentTaskId, count) => {
        try {
            apiRef.current.exec("update-task", {
                id: parentTaskId,
                task: { berater_count: count }
            });
        } catch (error) {
            console.error('Error updating berater count:', error);
        }
    };

    // Function to apply non-working day styling to Gantt chart columns
    const applyWorkingDayStyles = () => {
        setTimeout(() => {
            try {
                const workWeekConfig = getWorkWeekConfig();
                const scaleCells = document.querySelectorAll('.wx-scale-cell');

                scaleCells.forEach(cell => {
                    const cellDate = cell.getAttribute('data-date');
                    if (cellDate) {
                        const date = new Date(cellDate);
                        if (!isWorkingDay(date, workWeekConfig)) {
                            cell.classList.add('wx-non-working-day');

                            // Also apply to corresponding timeline columns
                            const dayColumns = document.querySelectorAll(`[data-date="${cellDate}"]`);
                            dayColumns.forEach(column => {
                                column.classList.add('wx-non-working');
                            });
                        } else {
                            cell.classList.remove('wx-non-working-day');
                            const dayColumns = document.querySelectorAll(`[data-date="${cellDate}"]`);
                            dayColumns.forEach(column => {
                                column.classList.remove('wx-non-working');
                            });
                        }
                    }
                });
            } catch (error) {
                console.error('Error applying working day styles:', error);
            }
        }, 100); // Small delay to ensure DOM is ready
    };

    // Function to format date inputs to dd.mm.yyyy format
    const formatDateInputs = () => {
        // Try multiple times with different delays to catch all cases
        const delays = [0, 50, 100, 200, 300];

        delays.forEach(delay => {
            setTimeout(() => {
                try {
                    const sidebar = document.querySelector('.wx-sidebar');

                    if (!sidebar) {
                        return;
                    }

                    // wx-react-gantt uses TEXT inputs, not date inputs!
                    // Find ALL text inputs in the sidebar
                    const allInputs = sidebar.querySelectorAll('input[type="text"]');

                    // Filter for date inputs (with values OR likely to be date inputs based on position)
                    // Take the first 2 text inputs that either have date values OR are not the first input (company search)
                    const dateInputs = Array.from(allInputs).filter((input, index) => {
                        const value = input.value;
                        const hasDateValue = value && (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/) || value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/));

                        // Skip the first input (company search) and any email inputs (has @)
                        const isLikelyDateInput = index > 0 && index <= 2 && !value.includes('@');

                        return hasDateValue || isLikelyDateInput;
                    }).slice(0, 2); // Take only first 2 date inputs


                dateInputs.forEach((dateInput, index) => {
                    // Skip if already converted
                    if (dateInput.hasAttribute('data-date-formatted')) {
                        return;
                    }

                    // Get the current value (in MM/DD/YYYY format from wx-react-gantt)
                    const currentValue = dateInput.value;

                    // Convert MM/DD/YYYY to DD.MM.YYYY format
                    let formattedValue = '';
                    let isoValue = '';
                    if (currentValue) {
                        // Check if already in DD.MM.YYYY format
                        if (currentValue.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                            formattedValue = currentValue;
                            const [day, month, year] = currentValue.split('.');
                            isoValue = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        }
                        // Convert from MM/DD/YYYY to DD.MM.YYYY
                        else if (currentValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                            const [month, day, year] = currentValue.split('/');
                            formattedValue = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
                            isoValue = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        }
                    } else {
                    }

                    // Mark this input as formatted BEFORE changing value to prevent re-processing
                    dateInput.setAttribute('data-date-formatted', 'true');
                    dateInput.setAttribute('data-original-format', 'mm/dd/yyyy'); // Store original format expected by library
                    if (isoValue) {
                        dateInput.setAttribute('data-iso-value', isoValue);
                    }

                    // Update the display value AFTER marking as formatted
                    if (formattedValue) {
                        dateInput.value = formattedValue;
                    }

                    // ALWAYS update placeholder to show expected format
                    dateInput.placeholder = 'TT.MM.JJJJ';

                    // Add input event listener if not already added
                    if (!dateInput.hasAttribute('data-input-listener-added')) {
                        dateInput.setAttribute('data-input-listener-added', 'true');

                        // MutationObserver to watch for library changing the value back to MM/DD/YYYY
                        const observer = new MutationObserver((mutations) => {
                            mutations.forEach((mutation) => {
                                if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                                    const newValue = dateInput.value;
                                    // If library set it to MM/DD/YYYY, convert to DD.MM.YYYY
                                    if (newValue && newValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                                        const [month, day, year] = newValue.split('/');
                                        const formatted = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
                                        dateInput.value = formatted;
                                        const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                                        dateInput.setAttribute('data-iso-value', isoDate);
                                    }
                                }
                            });
                        });
                        observer.observe(dateInput, { attributes: true, attributeFilter: ['value'] });

                        dateInput.addEventListener('input', (e) => {
                            let value = e.target.value;

                            // If user typed MM/DD/YYYY format, convert it
                            if (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                                const [month, day, year] = value.split('/');
                                value = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
                                e.target.value = value;
                            }

                            // Remove non-numeric characters except dots
                            value = value.replace(/[^\d.]/g, '');

                            // Limit to dd.mm.yyyy format
                            const parts = value.split('.');
                            if (parts[0] && parts[0].length > 2) {
                                parts[0] = parts[0].substring(0, 2);
                            }
                            if (parts[1] && parts[1].length > 2) {
                                parts[1] = parts[1].substring(0, 2);
                            }
                            if (parts[2] && parts[2].length > 4) {
                                parts[2] = parts[2].substring(0, 4);
                            }

                            value = parts.join('.');
                            e.target.value = value;

                            // Store the ISO value for later use
                            if (value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                                const [day, month, year] = value.split('.');
                                const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                                e.target.setAttribute('data-iso-value', isoDate);
                            }
                        });

                        // Add blur validation
                        dateInput.addEventListener('blur', (e) => {
                            const value = e.target.value;
                            if (value && !value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                                e.target.style.borderColor = '#ef4444';
                                setTimeout(() => {
                                    e.target.style.borderColor = '';
                                }, 2000);
                            }
                        });
                    }

                });

            } catch (error) {
                console.error('Error formatting date inputs:', error);
            }
            }, delay); // End setTimeout for each delay
        }); // End forEach for delays
    };

    // Function to inject work week selector into wx-gantt sidebar
    const injectWorkWeekSelector = () => {
        setTimeout(() => {
            try {
                const sidebar = document.querySelector('.wx-sidebar');
                const existingWorkWeek = document.querySelector('#work-week-selector-container');


                if (sidebar && !existingWorkWeek) {
                    // Create container for work week selector
                    const workWeekContainer = document.createElement('div');
                    workWeekContainer.id = 'work-week-selector-container';
                    workWeekContainer.style.cssText = `
                        margin: 16px 0;
                    `;

                    // Create the work week selector HTML
                    const workWeekConfig = getWorkWeekConfig();
                    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

                    workWeekContainer.innerHTML = `
                        <div style="margin-bottom: 12px;">
                            <label style="display: block; font-weight: 600; font-size: 14px; color: #374151; margin-bottom: 8px;">
                                Arbeitswoche konfigurieren
                            </label>

                            <!-- Preset buttons -->
                            <div style="margin-bottom: 12px; display: flex; gap: 8px;">
                                <button type="button" class="preset-btn" data-preset="mon-fri" style="
                                    padding: 4px 12px;
                                    font-size: 12px;
                                    background-color: #D9D9D9;
                                    color: #374151;
                                    border: none;
                                    border-radius: 4px;
                                    cursor: pointer;
                                ">Mo-Fr</button>
                                <button type="button" class="preset-btn" data-preset="mon-sat" style="
                                    padding: 4px 12px;
                                    font-size: 12px;
                                    background-color: #D9D9D9;
                                    color: #374151;
                                    border: none;
                                    border-radius: 4px;
                                    cursor: pointer;
                                ">Mo-Sa</button>
                                <button type="button" class="preset-btn" data-preset="all-days" style="
                                    padding: 4px 12px;
                                    font-size: 12px;
                                    background-color: #D9D9D9;
                                    color: #374151;
                                    border: none;
                                    border-radius: 4px;
                                    cursor: pointer;
                                ">Alle Tage</button>
                            </div>

                            <!-- Day selector buttons -->
                            <div style="display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 12px;" id="day-buttons-container">
                                ${days.map(dayName => {
                                    const dayInfo = DAY_NAMES[dayName];
                                    const isSelected = workWeekConfig[dayName];
                                    return `
                                        <button type="button"
                                                class="day-btn"
                                                data-day="${dayName}"
                                                title="${dayInfo.full}"
                                                style="
                                                    width: 42px;
                                                    height: 36px;
                                                    border-radius: 6px;
                                                    border: 2px solid;
                                                    display: flex;
                                                    align-items: center;
                                                    justify-content: center;
                                                    font-weight: 600;
                                                    font-size: 13px;
                                                    cursor: pointer;
                                                    transition: all 0.2s ease;
                                                    ${isSelected ?
                                                        'background-color: #3A46A9; color: white; border-color: #3A46A9;' :
                                                        'background-color: white; color: #3A46A9; border-color: #d1d5db;'
                                                    }
                                                ">
                                            ${dayInfo.short}
                                        </button>
                                    `;
                                }).join('')}
                            </div>

                            <!-- Current selection display -->
                            <div style="font-size: 12px; color: #6b7280;" id="work-week-display">
                                <strong>Gewählte Arbeitswoche:</strong>
                                <span id="selected-days">
                                    ${days.filter(day => workWeekConfig[day]).map(day => DAY_NAMES[day].short).join(' - ') || 'Keine Tage ausgewählt'}
                                </span>
                            </div>
                        </div>
                    `;

                    // Try multiple insertion strategies
                    let inserted = false;

                    // Strategy 1: Insert after the details/textarea field
                    const detailsField = sidebar.querySelector('textarea')?.closest('.wx-field');
                    if (detailsField && !inserted) {
                        detailsField.parentNode.insertBefore(workWeekContainer, detailsField.nextSibling);
                        inserted = true;
                    }

                    // Strategy 2: Insert after any field
                    if (!inserted) {
                        const allFields = sidebar.querySelectorAll('.wx-field');
                        const lastField = allFields[allFields.length - 1];
                        if (lastField) {
                            lastField.parentNode.insertBefore(workWeekContainer, lastField.nextSibling);
                            inserted = true;
                        }
                    }

                    // Strategy 3: Direct append to sidebar
                    if (!inserted) {
                        sidebar.appendChild(workWeekContainer);
                        inserted = true;
                    }


                    // Add event listeners for day buttons
                    const dayButtons = workWeekContainer.querySelectorAll('.day-btn');
                    dayButtons.forEach(btn => {
                        btn.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            const dayName = btn.getAttribute('data-day');
                            const currentConfig = getWorkWeekConfig();
                            const newConfig = {
                                ...currentConfig,
                                [dayName]: !currentConfig[dayName]
                            };

                            setWorkWeekConfig(newConfig);
                            updateDayButtonsDisplay(workWeekContainer, newConfig);
                            applyWorkingDayStyles();

                            // Store configuration in work week container data attribute
                            workWeekContainer.setAttribute('data-work-week-config', JSON.stringify(newConfig));
                        });
                    });

                    // Add event listeners for preset buttons
                    const presetButtons = workWeekContainer.querySelectorAll('.preset-btn');
                    presetButtons.forEach(btn => {
                        btn.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            const preset = btn.getAttribute('data-preset');
                            let newConfig;

                            switch (preset) {
                                case 'mon-fri':
                                    newConfig = {
                                        monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
                                        saturday: false, sunday: false
                                    };
                                    break;
                                case 'mon-sat':
                                    newConfig = {
                                        monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
                                        saturday: true, sunday: false
                                    };
                                    break;
                                case 'all-days':
                                    newConfig = {
                                        monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
                                        saturday: true, sunday: true
                                    };
                                    break;
                                default:
                                    return;
                            }

                            setWorkWeekConfig(newConfig);
                            updateDayButtonsDisplay(workWeekContainer, newConfig);
                            applyWorkingDayStyles();

                            // Store configuration in work week container data attribute
                            workWeekContainer.setAttribute('data-work-week-config', JSON.stringify(newConfig));
                        });
                    });
                }
            } catch (error) {
                console.error('Error injecting work week selector:', error);
            }
        }, 400); // Increased delay to give more time for DOM to be ready
    };

    // Function to hide the FP-ID field created by wx-react-gantt
    const hideFPIDField = () => {
        setTimeout(() => {
            try {
                // Find the FP-ID field and its container
                const fpIdInput = document.querySelector('.wx-sidebar input[name="FP_ID"]') ||
                                 document.querySelector('.wx-sidebar input[placeholder*="FP-ID"]') ||
                                 document.querySelector('.wx-sidebar input[type="number"]');

                if (fpIdInput) {
                    // Find the parent container (usually .wx-field)
                    const fpIdContainer = fpIdInput.closest('.wx-field') || fpIdInput.parentElement;

                    if (fpIdContainer) {
                        fpIdContainer.style.display = 'none';
                        fpIdContainer.style.visibility = 'hidden';
                        fpIdContainer.style.height = '0';
                        fpIdContainer.style.overflow = 'hidden';

                        // But keep the input accessible for form submission
                        fpIdInput.style.display = 'none';
                        fpIdInput.setAttribute('data-hidden-fpid', 'true');
                    } else {
                        // Just hide the input directly
                        fpIdInput.style.display = 'none';
                        fpIdInput.style.visibility = 'hidden';
                        fpIdInput.setAttribute('data-hidden-fpid', 'true');
                    }
                } else {
                }
            } catch (error) {
                console.error('Error hiding FP-ID field:', error);
            }
        }, 100);
    };

    // Function to inject hidden FP-ID field programmatically
    const injectHiddenFPIDField = () => {
        setTimeout(() => {
            try {
                const sidebar = document.querySelector('.wx-sidebar');
                if (!sidebar) {
                    return;
                }

                // Check if hidden FP-ID field already exists
                let fpIdField = document.querySelector('.wx-sidebar input[name="FP_ID"]') ||
                               document.querySelector('.wx-sidebar input[data-key="FP_ID"]') ||
                               document.querySelector('#hidden-fp-id-field');

                if (!fpIdField) {

                    // Create hidden input field
                    fpIdField = document.createElement('input');
                    fpIdField.type = 'hidden';
                    fpIdField.name = 'FP_ID';
                    fpIdField.id = 'hidden-fp-id-field';
                    fpIdField.setAttribute('data-key', 'FP_ID');
                    fpIdField.value = ''; // Will be populated by company search

                    // Add to sidebar
                    sidebar.appendChild(fpIdField);
                } else {
                }
            } catch (error) {
                console.error('Error injecting hidden FP-ID field:', error);
            }
        }, 100);
    };

    // Function to load work week configuration from data attribute or details field
    const loadWorkWeekFromDetails = () => {
        try {
            const workWeekContainer = document.querySelector('#work-week-selector-container');
            const detailsField = document.querySelector('.wx-sidebar textarea') ||
                                document.querySelector('.wx-sidebar input[data-key="details"]');

            // First try loading from data attribute
            if (workWeekContainer) {
                const storedConfig = workWeekContainer.getAttribute('data-work-week-config');
                if (storedConfig) {
                    try {
                        const config = JSON.parse(storedConfig);
                        setWorkWeekConfig(config);
                        updateDayButtonsDisplay(workWeekContainer, config);
                        return; // Successfully loaded from data attribute
                    } catch (e) {
                        // Invalid JSON, continue to try details field
                    }
                }
            }

            // Fall back to parsing details field for backward compatibility
            if (detailsField && detailsField.value) {
                const detailsValue = detailsField.value;
                const workWeekMatch = detailsValue.match(/Arbeitstage:\s*([^\n]+)/);

                if (workWeekMatch) {
                    const workingDaysText = workWeekMatch[1];

                    const dayNamesReverse = {
                        'Montag': 'monday',
                        'Dienstag': 'tuesday',
                        'Mittwoch': 'wednesday',
                        'Donnerstag': 'thursday',
                        'Freitag': 'friday',
                        'Samstag': 'saturday',
                        'Sonntag': 'sunday'
                    };

                    // Create config with all days false initially
                    const config = {
                        monday: false,
                        tuesday: false,
                        wednesday: false,
                        thursday: false,
                        friday: false,
                        saturday: false,
                        sunday: false
                    };

                    // Enable days mentioned in the text
                    Object.entries(dayNamesReverse).forEach(([germanDay, englishDay]) => {
                        if (workingDaysText.includes(germanDay)) {
                            config[englishDay] = true;
                        }
                    });

                    setWorkWeekConfig(config);

                    // Store in data attribute for future use
                    if (workWeekContainer) {
                        workWeekContainer.setAttribute('data-work-week-config', JSON.stringify(config));
                        updateDayButtonsDisplay(workWeekContainer, config);
                    }

                    // Clean up details field - remove auto-generated work week text
                    const cleanedDetails = detailsValue.replace(/Arbeitstage:.*?(?=\n|$)/g, '').trim();
                    detailsField.value = cleanedDetails;
                }
            }
        } catch (error) {
            console.error('Error loading work week from details:', error);
        }
    };

    // Function to update details field with work week configuration
    const updateDetailsFieldWithWorkWeek = (config) => {
        try {
            const detailsField = document.querySelector('.wx-sidebar textarea') ||
                                document.querySelector('.wx-sidebar input[data-key="details"]');

            if (detailsField) {
                const dayNames = {
                    monday: 'Montag',
                    tuesday: 'Dienstag',
                    wednesday: 'Mittwoch',
                    thursday: 'Donnerstag',
                    friday: 'Freitag',
                    saturday: 'Samstag',
                    sunday: 'Sonntag'
                };

                const workingDays = Object.entries(config)
                    .filter(([day, isWorking]) => isWorking)
                    .map(([day]) => dayNames[day])
                    .join(', ');

                const workWeekText = `Arbeitstage: ${workingDays}`;

                // Get current value and check if work week info already exists
                let currentValue = detailsField.value || '';

                // Remove existing work week configuration if present
                currentValue = currentValue.replace(/Arbeitstage:.*?(?=\n|$)/g, '').trim();

                // Add new work week configuration
                if (currentValue) {
                    detailsField.value = currentValue + '\n' + workWeekText;
                } else {
                    detailsField.value = workWeekText;
                }

            }
        } catch (error) {
            console.error('Error updating details field with work week:', error);
        }
    };

    // Function to update day buttons display
    const updateDayButtonsDisplay = (container, config) => {
        const dayButtons = container.querySelectorAll('.day-btn');
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        dayButtons.forEach(btn => {
            const dayName = btn.getAttribute('data-day');
            const isSelected = config[dayName];

            if (isSelected) {
                btn.style.backgroundColor = '#3A46A9';
                btn.style.color = 'white';
                btn.style.borderColor = '#3A46A9';
            } else {
                btn.style.backgroundColor = 'white';
                btn.style.color = '#3A46A9';
                btn.style.borderColor = '#d1d5db';
            }
        });

        // Update display text
        const selectedDaysSpan = container.querySelector('#selected-days');
        if (selectedDaysSpan) {
            const selectedText = days.filter(day => config[day]).map(day => DAY_NAMES[day].short).join(' - ') || 'Keine Tage ausgewählt';
            selectedDaysSpan.textContent = selectedText;
        }
    };

    // Function to inject company search functionality into the sidebar
    const injectCompanySearch = () => {
        try {
            setTimeout(() => {

                // Look for the company search field in the sidebar with multiple selectors
                let companySearchField = document.querySelector('input[name="companySearch"]');

                // Try alternative selectors if first one doesn't work
                if (!companySearchField) {
                    companySearchField = document.querySelector('input[data-key="companySearch"]');
                }
                if (!companySearchField) {
                    companySearchField = document.querySelector('input[placeholder*="Firmennamen"]');
                }
                if (!companySearchField) {
                    // Look for any input in the sidebar with the label
                    const allInputs = document.querySelectorAll('.wx-sidebar input[type="text"]');
                    allInputs.forEach((input, index) => {
                        console.log({ index, input });
                    });
                    // Try to find by looking at the first text input in the sidebar
                    companySearchField = allInputs[0];
                }


                // Always check for edit mode and pre-populate fields
                setTimeout(() => {
                    if (isEditMode && editingTaskData) {

                        // Pre-populate company search field
                        if (companySearchField) {
                            companySearchField.value = editingTaskData.text || '';
                        }

                        // Pre-populate FP_ID field
                        const fpIdField = document.querySelector('.wx-sidebar input[type="number"]') ||
                                         document.querySelector('.wx-sidebar input[name="FP_ID"]') ||
                                         document.querySelector('.wx-sidebar input[data-key="FP_ID"]');
                        if (fpIdField) {
                            fpIdField.value = editingTaskData.id || '';
                        }

                        // Pre-populate date fields - wx-react-gantt uses text inputs
                        // Find ALL text inputs, then filter for date-like values
                        const dateTextInputs = document.querySelectorAll('.wx-sidebar input[type="text"]');
                        const dateInputs = Array.from(dateTextInputs).filter(input => {
                            const value = input.value;
                            // Empty inputs or inputs with date-like values
                            return !value || value.match(/^\d{1,2}[\/\.]\d{1,2}\/\d{4}$/);
                        }).slice(0, 2); // Take first 2 date inputs


                        if (dateInputs.length >= 2 && editingTaskData.start && editingTaskData.end) {
                            const startDate = new Date(editingTaskData.start);
                            const endDate = new Date(editingTaskData.end);

                            // Format as DD.MM.YYYY
                            const startFormatted = `${String(startDate.getDate()).padStart(2, '0')}.${String(startDate.getMonth() + 1).padStart(2, '0')}.${startDate.getFullYear()}`;
                            const endFormatted = `${String(endDate.getDate()).padStart(2, '0')}.${String(endDate.getMonth() + 1).padStart(2, '0')}.${endDate.getFullYear()}`;

                            dateInputs[0].value = startFormatted;
                            dateInputs[1].value = endFormatted;

                            // Mark as formatted and store ISO values
                            dateInputs[0].setAttribute('data-date-formatted', 'true');
                            dateInputs[1].setAttribute('data-date-formatted', 'true');
                            dateInputs[0].setAttribute('data-iso-value', startDate.toISOString().split('T')[0]);
                            dateInputs[1].setAttribute('data-iso-value', endDate.toISOString().split('T')[0]);

                            // Update placeholders
                            dateInputs[0].placeholder = 'TT.MM.JJJJ';
                            dateInputs[1].placeholder = 'TT.MM.JJJJ';

                        }

                        // Pre-populate details field
                        const detailsField = document.querySelector('.wx-sidebar textarea') ||
                                            document.querySelector('.wx-sidebar input[data-key="details"]');
                        if (detailsField && editingTaskData.details) {
                            detailsField.value = editingTaskData.details;
                        }
                    }
                }, 500); // Increased delay to ensure fields are rendered

                if (companySearchField && !companySearchField.hasAttribute('data-search-injected')) {
                    companySearchField.setAttribute('data-search-injected', 'true');

                    // Convert to searchable dropdown
                    const parentDiv = companySearchField.closest('.wx-field');
                    if (parentDiv && !parentDiv.querySelector('.company-search-results')) {
                        // Create search results container only if it doesn't exist
                        const resultsContainer = document.createElement('div');
                        resultsContainer.className = 'company-search-results';
                        resultsContainer.style.cssText = `
                            position: absolute;
                            top: 100%;
                            left: 0;
                            right: 0;
                            background: white;
                            border: 1px solid #d1d5db;
                            border-top: none;
                            max-height: 200px;
                            overflow-y: auto;
                            z-index: 1000;
                            display: none;
                        `;

                        // Make parent relative for positioning
                        parentDiv.style.position = 'relative';
                        parentDiv.appendChild(resultsContainer);

                        let searchTimeout;

                        // Add search functionality

                        const handleInput = (e) => {
                            // Prevent event bubbling to avoid triggering other form events
                            e.stopPropagation();


                            const searchTerm = e.target.value.trim();

                            clearTimeout(searchTimeout);

                            if (searchTerm.length < 2) {
                                resultsContainer.style.display = 'none';
                                return;
                            }

                            // Debounce search
                            searchTimeout = setTimeout(() => {

                                try {
                                    searchCompanies(searchTerm, resultsContainer, companySearchField);
                                } catch (error) {
                                    console.error('Error in searchCompanies call:', error);
                                    resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #ef4444;">Fehler beim Suchen</div>';
                                    resultsContainer.style.display = 'block';
                                }
                            }, 500); // Increased debounce to reduce API calls
                        };

                        // Remove existing event listeners before adding new ones
                        companySearchField.removeEventListener('input', handleInput);
                        companySearchField.addEventListener('input', handleInput);

                        // Hide results when clicking outside
                        const handleClickOutside = (e) => {
                            if (!parentDiv.contains(e.target)) {
                                resultsContainer.style.display = 'none';
                            }
                        };

                        document.removeEventListener('click', handleClickOutside);
                        document.addEventListener('click', handleClickOutside);
                    }
                }
            }, 200);
        } catch (error) {
            console.error('Error injecting company search:', error);
        }
    };

    // Function to search companies via API
    const searchCompanies = async (searchTerm, resultsContainer, inputField) => {
        try {

            if (!portal) {
                console.error('Portal not available for search');
                resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #ef4444;">Portal nicht verfügbar</div>';
                resultsContainer.style.display = 'block';
                return;
            }

            // Get list of already displayed company FP_IDs to exclude from search results
            const existingFPIds = tasks.map(task => task.id).filter(id => id && !isNaN(id));

            // Show loading state
            resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #6b7280;">Suche läuft...</div>';
            resultsContainer.style.display = 'block';

            const formData = new FormData();
            formData.append('portal', portal);
            formData.append('userID', 1);
            formData.append('rows', '10'); // Match the working payload
            formData.append('page', 1);
            formData.append('sortColumn', '7'); // Match the working payload
            formData.append('sortMethod', 'asc');

            // Set up filters for company search using the correct structure
            const milestoneFilterID = {
                a: 1, b: null, c: null, d: null, e: null, f: null,
                g: null, h: 111, i: null, j: null, k: 114, l: null
            };

            const milestoneFilter = {
                a: searchTerm, b: null, c: null, d: null, e: null,
                f: null, g: null, h: 1, i: 0, j: 0, k: 1, l: 0
            };

            const milestoneDateFilter = { id: null, value: null };

            formData.append('filterID', JSON.stringify(milestoneFilterID));
            formData.append('filter', JSON.stringify(milestoneFilter));
            formData.append('dateFilter', JSON.stringify(milestoneDateFilter));


            const response = await Api().post('getBestands', formData);

            // Clear previous results
            resultsContainer.innerHTML = '';

            if (response.data && response.data.bestands && response.data.bestands.length > 0) {
                // Filter out companies that are already displayed in the Gantt chart
                const filteredCompanies = response.data.bestands.filter(company => {
                    const fpId = parseInt(company.FP_ID);
                    const isAlreadyDisplayed = existingFPIds.includes(fpId);
                    if (isAlreadyDisplayed) {
                    }
                    return !isAlreadyDisplayed;
                });


                if (filteredCompanies.length === 0) {
                    resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #6b7280;">Alle gefundenen Firmen sind bereits im Kalender</div>';
                    resultsContainer.style.display = 'block';
                    return;
                }

                filteredCompanies.forEach(company => {
                    const resultItem = document.createElement('div');
                    resultItem.className = 'company-search-item';
                    resultItem.style.cssText = `
                        padding: 8px 12px;
                        cursor: pointer;
                        border-bottom: 1px solid #f3f4f6;
                    `;

                    resultItem.innerHTML = `
                        <div style="font-weight: 500;">${company.FirmaKurz || company.Firma}</div>
                        <div style="font-size: 12px; color: #6b7280;">FP-ID: ${company.FP_ID}</div>
                    `;

                    // Add hover effects
                    resultItem.addEventListener('mouseenter', () => {
                        resultItem.style.backgroundColor = '#f9fafb';
                    });

                    resultItem.addEventListener('mouseleave', () => {
                        resultItem.style.backgroundColor = 'white';
                    });

                    // Handle selection
                    resultItem.addEventListener('click', () => {

                        inputField.value = company.FirmaKurz || company.Firma;

                        // Also populate the FP_ID field with multiple selector attempts
                        const fpIdField = document.querySelector('input[name="FP_ID"]') ||
                                         document.querySelector('input[data-key="FP_ID"]') ||
                                         document.querySelector('.wx-sidebar input[type="number"]');

                        if (fpIdField) {
                            fpIdField.value = company.FP_ID;

                            // Trigger change event to ensure the value is registered
                            fpIdField.dispatchEvent(new Event('input', { bubbles: true }));
                            fpIdField.dispatchEvent(new Event('change', { bubbles: true }));

                            // ALSO set the hidden FP_ID field if it exists
                            const hiddenFpIdField = document.querySelector('#hidden-fp-id-field');
                            if (hiddenFpIdField) {
                                hiddenFpIdField.value = company.FP_ID;
                            }

                            // Store in a global variable as backup
                            window.selectedCompanyFPID = company.FP_ID;
                            window.selectedCompanyName = company.FirmaKurz || company.Firma;

                            // Also store in the form data attribute for reliable retrieval
                            fpIdField.setAttribute('data-selected-fp-id', company.FP_ID);

                            // Try to trigger wx-react-gantt's update mechanism
                            try {
                                if (apiRef.current) {
                                    // Force the gantt to recognize the field update
                                    const formElement = fpIdField.closest('form') || fpIdField.closest('.wx-sidebar');
                                    if (formElement) {
                                        const event = new Event('change', { bubbles: true, cancelable: true });
                                        formElement.dispatchEvent(event);
                                    }
                                }
                            } catch (error) {
                            }
                        } else {
                            const allInputs = document.querySelectorAll('.wx-sidebar input');
                            allInputs.forEach((input, index) => {
                                console.log({
                                    name: input.name,
                                    type: input.type,
                                    placeholder: input.placeholder,
                                    value: input.value
                                });
                            });

                            // Store in global variables even if field not found
                            window.selectedCompanyFPID = company.FP_ID;
                            window.selectedCompanyName = company.FirmaKurz || company.Firma;
                        }

                        // Update text field if it exists (this will be the task name)
                        const textField = document.querySelector('input[name="text"]') ||
                                         document.querySelector('input[data-key="text"]');
                        if (textField) {
                            textField.value = `${company.FirmaKurz || company.Firma} - MA-Beratungslauf`;
                            textField.dispatchEvent(new Event('input', { bubbles: true }));
                            textField.dispatchEvent(new Event('change', { bubbles: true }));
                        }

                        resultsContainer.style.display = 'none';
                    });

                    resultsContainer.appendChild(resultItem);
                });

                resultsContainer.style.display = 'block';
            } else {
                resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #6b7280;">Keine Ergebnisse gefunden<br><small>Bereits angezeigte Firmen sind ausgeblendet</small></div>';
                resultsContainer.style.display = 'block';
            }
        } catch (error) {
            console.error('Error searching companies:', error);
            resultsContainer.innerHTML = '<div style="padding: 8px 12px; color: #ef4444;">Fehler beim Suchen</div>';
            resultsContainer.style.display = 'block';
        }
    };

    // Helper function to find and update all Gantt body containers
    const updateGanttContainerHeights = (heightDelta) => {
        const ganttContainer = document.querySelector('.wx-gantt');
        if (!ganttContainer) {
            console.error('Gantt container not found!');
            return;
        }

        // Find the wx-body (grid)
        const wxBody = ganttContainer.querySelector('.wx-body');
        const wxBodyHeight = wxBody ? parseInt(wxBody.style.height) : 0;

        // Find ALL divs with inline height style
        const allDivsWithHeight = ganttContainer.querySelectorAll('div[style*="height"]');
        const chartContainers = [];

        allDivsWithHeight.forEach(div => {
            const divHeight = parseInt(div.style.height) || 0;
            // If height matches wx-body (within 5px) and it's not wx-body itself, it's likely the chart container
            if (Math.abs(divHeight - wxBodyHeight) < 5 && div !== wxBody && !div.classList.contains('wx-body')) {
                chartContainers.push(div);
            }
        });

        // Update all containers
        const containersToUpdate = [wxBody, ...chartContainers].filter(Boolean);

        containersToUpdate.forEach((container, index) => {
            const currentHeight = parseInt(container.style.height) || 0;
            const newHeight = Math.max(0, currentHeight + heightDelta);
            container.style.height = `${newHeight}px`;
        });

        // Force layout recalculation to ensure perfect sync
        setTimeout(() => {
            // Trigger a reflow by reading offsetHeight
            containersToUpdate.forEach(container => {
                void container.offsetHeight;
            });
        }, 10);
    };

    // Function to show permanent "Add Berater" button after consultant list as a Gantt task
    // This allows users to add multiple consultants to an FP after initial consultants are loaded
    const showAddConsultantButton = (taskId) => {
        try {
            const buttonTaskId = `add_consultant_button_${taskId}`;

            // Check if button task already exists
            const existingButtonTask = tasks.find(task => task.id === buttonTaskId);
            if (existingButtonTask) {
                console.log(`Button task already exists for ${taskId}`);
                return;
            }

            // Get parent task to extract start/end dates
            const parentTask = tasks.find(task => task.id === taskId);
            if (!parentTask) {
                console.error(`Parent task ${taskId} not found`);
                return;
            }

            // Create button task (similar to no consultants task)
            const buttonTask = {
                id: buttonTaskId,
                text: "Weitere Berater zu diesem FP hinzufügen",
                start: new Date(parentTask.start),
                end: new Date(parentTask.end),
                duration: parentTask.duration || Math.ceil((new Date(parentTask.end).getTime() - new Date(parentTask.start).getTime()) / (24 * 60 * 60 * 1000)),
                progress: 0,
                type: "add-button", // Custom type for button tasks
                parent: taskId,
                berater_name: "",
                berater_count: 0,
                links: [],
                fpId: taskId,
                lazy: false, // Not expandable
                readonly: true // Cannot be edited
            };

            // Add button task as a child of the FP task
            apiRef.current.exec("add-task", {
                task: buttonTask,
                target: taskId,
                mode: "child"
            });

            console.log(`✓ Added button task for ${taskId}`);

            // Add click handler after task renders
            setTimeout(() => {
                addClickHandlerToButtonTask(buttonTaskId, taskId);
            }, 500);

        } catch (error) {
            console.error('Error adding button task:', error);
        }
    };

    // Function to add click handler to button task row
    const addClickHandlerToButtonTask = (buttonTaskId, parentTaskId) => {
        try {
            // Find the rendered button task row in the Gantt grid
            const buttonRow = document.querySelector(`[data-id="${buttonTaskId}"]`);

            if (!buttonRow) {
                console.error(`Button task row not found for ${buttonTaskId}`);
                return;
            }

            // Hide the date columns (Start, Tage, Ende)
            const cells = buttonRow.querySelectorAll('.wx-cell');
            if (cells.length >= 4) {
                // Hide Start column (2nd cell)
                if (cells[1]) cells[1].style.visibility = 'hidden';
                // Hide Tage column (3rd cell)
                if (cells[2]) cells[2].style.visibility = 'hidden';
                // Hide Ende column (4th cell)
                if (cells[3]) cells[3].style.visibility = 'hidden';
            }

            // Find the first cell (text cell) and replace text with button
            const firstCell = cells[0];
            if (firstCell) {
                // Look for .wx-content (Gantt library uses this class)
                const cellContent = firstCell.querySelector('.wx-content');
                if (cellContent) {
                    // Clear the cell content (remove toggle placeholder and text div)
                    cellContent.innerHTML = '';

                    // Create button element
                    const button = document.createElement('button');
                    button.style.cssText = `
                        background-color: #3A46A9;
                        color: white;
                        border: none;
                        padding: 4px 12px;
                        border-radius: 4px;
                        font-size: 13px;
                        font-weight: 500;
                        cursor: pointer;
                        white-space: nowrap;
                        transition: background-color 0.2s ease;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        margin-left: 0;
                    `;

                    // Add plus icon
                    const icon = document.createElement('span');
                    icon.textContent = '+';
                    icon.style.cssText = `
                        font-size: 16px;
                        font-weight: bold;
                    `;

                    // Add button text
                    const buttonText = document.createElement('span');
                    buttonText.textContent = 'Weitere Berater zu diesem FP hinzufügen';

                    // Append icon and text to button
                    button.appendChild(icon);
                    button.appendChild(buttonText);

                    // Add click handler to button
                    button.addEventListener('click', (e) => {
                        e.stopPropagation();
                        // Don't clear UI when adding more consultants (shouldClearUI = false)
                        showAddConsultantDialog(parentTaskId, false);
                    });

                    // Add hover effect
                    button.addEventListener('mouseenter', () => {
                        button.style.backgroundColor = '#2e3782';
                    });
                    button.addEventListener('mouseleave', () => {
                        button.style.backgroundColor = '#3A46A9';
                    });

                    // Append button to cell content
                    cellContent.appendChild(button);

                    // Override the padding-left that Gantt adds
                    cellContent.style.paddingLeft = '12px';
                }
            }

            // Remove row background color and cursor since button handles it
            buttonRow.style.cursor = 'default';

            console.log(`✓ Added button to task ${buttonTaskId}`);

        } catch (error) {
            console.error('Error adding button to task:', error);
        }
    };

    // Function to show message under FP row
    const showMessageUnderFP = (taskId, message) => {
        try {

            // Find the task row element in the grid
            const taskRow = document.querySelector(`[data-id="${taskId}"]`);

            if (taskRow) {
                // Remove any existing message
                const existingMessage = document.querySelector(`[data-message-for="${taskId}"]`);
                if (existingMessage) {
                    existingMessage.remove();
                }

                // Create message element that matches Gantt row structure
                const messageRow = document.createElement('div');
                messageRow.setAttribute('data-message-for', taskId);
                messageRow.className = 'wx-row fp-message-row';
                messageRow.style.cssText = `
                    min-height: 40px;
                    display: flex;
                    align-items: center;
                    background-color: #f9fafb;
                    border-left: 4px solid #3A46A9;
                    padding: 8px 12px;
                    margin: 0;
                `;

                // Create message content with button
                messageRow.innerHTML = `
                    <div class="fp-message-content" style="display: flex; align-items: center; justify-content: space-between; width: 100%; gap: 16px;">
                        <div class="fp-message-info" style="display: flex; align-items: center; gap: 8px; color: #374151;">
                            <i class="fas fa-info-circle fp-message-icon" style="color: #3A46A9;"></i>
                            <span style="font-size: 13px; font-weight: 500; font-style: normal;">${message}</span>
                        </div>
                        <button class="add-consultant-btn" style="background-color: #3A46A9; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 12px; cursor: pointer; white-space: nowrap;">
                            <i class="fas fa-plus"></i>
                            Berater hinzufügen
                        </button>
                    </div>
                `;

                // Add click handler to the button
                const addButton = messageRow.querySelector('.add-consultant-btn');
                addButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    // Don't clear UI when opening modal (shouldClearUI = false)
                    showAddConsultantDialog(taskId, false);
                    // Keep message visible - don't remove it
                });

                // Hover effects
                addButton.addEventListener('mouseenter', () => {
                    addButton.style.backgroundColor = '#2e3782';
                });
                addButton.addEventListener('mouseleave', () => {
                    addButton.style.backgroundColor = '#3A46A9';
                });


                // Find row index BEFORE inserting the message row
                // Search upward to find the grid body container
                const ganttContainer = document.querySelector('.wx-gantt');
                const gridBody = ganttContainer ? ganttContainer.querySelector('.wx-body') : null;

                if (!gridBody) {
                    console.error('Grid body not found!');
                }

                const allGridRowsBefore = gridBody ? Array.from(gridBody.querySelectorAll('[data-id]')) : [];
                const expandedRowIndex = allGridRowsBefore.findIndex(row => {
                    const rowId = row.getAttribute('data-id');
                    return rowId == taskId; // Use == instead of === to handle string/number mismatch
                });


                // Insert the message row right after the FP row
                taskRow.parentNode.insertBefore(messageRow, taskRow.nextSibling);

                // Shift ALL timeline elements below this firma downward
                setTimeout(() => {
                    const ganttContainer = document.querySelector('.wx-gantt');
                    if (!ganttContainer) return;


                    // Find the timeline area
                    const wxArea = ganttContainer.querySelector('.wx-area');
                    if (!wxArea) {
                        return;
                    }

                    // Find ALL elements in timeline (bars, links, etc.)
                    const timelineElements = wxArea.querySelectorAll('[data-task-id], .wx-bar, .wx-link');

                    // Log all timeline elements to understand structure
                    timelineElements.forEach((el, i) => {
                        const tid = el.getAttribute('data-task-id') || el.getAttribute('data-id');
                        const top = el.style.top;
                    });

                    // Shift each element that belongs to a firma STRICTLY below the expanded one
                    timelineElements.forEach((element) => {
                        const elementTaskId = element.getAttribute('data-task-id') ||
                                             element.getAttribute('data-id');

                        if (elementTaskId && elementTaskId !== taskId) { // Don't shift the expanded firma's own timeline
                            // Find which row this element belongs to (using the BEFORE grid rows)
                            const elementRowIndex = allGridRowsBefore.findIndex(row =>
                                row.getAttribute('data-id') === elementTaskId
                            );


                            // Only shift if this element is STRICTLY BELOW (not equal to) the expanded firma
                            if (elementRowIndex > expandedRowIndex && elementRowIndex !== -1) {
                                const currentTop = parseInt(element.style.top) || 0;
                                const newTop = currentTop + 45; // Message row height
                                element.style.top = `${newTop}px`;
                                element.setAttribute('data-shifted-by', taskId);
                                element.setAttribute('data-shift-amount', '45');
                            } else {
                            }
                        } else {
                        }
                    });
                }, 100);

                // Increase height for message row (45px)
                setTimeout(() => {
                    updateGanttContainerHeights(45);
                }, 100);

                // Add expanded class to toggle icon to show [-] icon
                const toggleIcon = taskRow.querySelector('.wx-toggle-icon');
                if (toggleIcon) {
                    toggleIcon.classList.add('wx-custom-expanded');
                }

            }
        } catch (error) {
            console.error('Error in showMessageUnderFP:', error);
            // Fallback to toast if DOM manipulation fails
            toast.info(message);
        }
    };

    // Function to clear add consultant button task
    const clearAddConsultantButton = (taskId) => {
        try {
            const buttonTaskId = `add_consultant_button_${taskId}`;

            // Check if button task exists
            const existingButtonTask = tasks.find(task => task.id === buttonTaskId);
            if (existingButtonTask && apiRef.current) {
                // Delete the button task using Gantt API
                apiRef.current.exec("delete-task", { id: buttonTaskId });
                console.log(`✓ Removed add consultant button task for ${taskId}`);
            }
        } catch (error) {
            // Silent fail
        }
    };

    // Function to clear message for FP
    const clearMessageForFP = (taskId) => {
        try {
            const existingMessage = document.querySelector(`[data-message-for="${taskId}"]`);
            if (existingMessage) {
                existingMessage.remove();

                // Shift back timeline elements that were shifted down
                const ganttContainer = document.querySelector('.wx-gantt');
                if (ganttContainer) {
                    const shiftedElements = ganttContainer.querySelectorAll(`[data-shifted-by="${taskId}"]`);

                    shiftedElements.forEach(element => {
                        const shiftAmount = parseInt(element.getAttribute('data-shift-amount')) || 45;
                        const currentTop = parseInt(element.style.top) || 0;
                        element.style.top = `${currentTop - shiftAmount}px`;
                        element.removeAttribute('data-shifted-by');
                        element.removeAttribute('data-shift-amount');
                    });
                }

                // Decrease height for message row removal (-45px)
                setTimeout(() => {
                    updateGanttContainerHeights(-45);
                }, 10);
            }

            // Also clear add consultant button when clearing message
            clearAddConsultantButton(taskId);
        } catch (error) {
            // Silent fail
        }
    };

    // Function to clear "no consultants" task from Gantt
    const clearNoConsultantsTask = (parentTaskId) => {
        try {
            const noConsultantsTaskId = `no_consultants_${parentTaskId}`;

            // Check if the task exists before trying to delete it
            const taskExists = tasks.some(task => task.id === noConsultantsTaskId);

            if (taskExists) {
                // Try to delete the task from the Gantt chart
                apiRef.current.exec("delete-task", { id: noConsultantsTaskId });
            } else {
            }
        } catch (error) {
        }
    };

    // Function to inject missing FP_ID field if not present
    const injectMissingFPIDField = () => {
        try {
            const sidebar = document.querySelector('.wx-sidebar');
            if (!sidebar) {
                return;
            }

            // Check if FP_ID field already exists
            const fpIdField = document.querySelector('.wx-sidebar input[type="number"]') ||
                             document.querySelector('.wx-sidebar input[name="FP_ID"]') ||
                             document.querySelector('.wx-sidebar input[data-key="FP_ID"]');

            if (fpIdField) {
                return;
            }


            // Find the company search field to insert after it
            const companyField = document.querySelector('.wx-sidebar input[placeholder*="Firmennamen"]');
            if (!companyField) {
                return;
            }

            const companyFieldContainer = companyField.closest('.wx-field') || companyField.parentNode;

            // Create FP_ID field container
            const fpIdContainer = document.createElement('div');
            fpIdContainer.className = 'wx-field';
            fpIdContainer.style.cssText = 'margin-bottom: 12px;';

            // Create label
            const fpIdLabel = document.createElement('label');
            fpIdLabel.textContent = 'FP-ID';
            fpIdLabel.style.cssText = `
                display: block;
                margin-bottom: 4px;
                font-weight: 500;
                font-size: 13px;
                color: #374151;
            `;

            // Create input
            const fpIdInput = document.createElement('input');
            fpIdInput.type = 'number';
            fpIdInput.name = 'FP_ID';
            fpIdInput.setAttribute('data-key', 'FP_ID');
            fpIdInput.placeholder = 'FP-ID eingeben';
            fpIdInput.required = true;
            fpIdInput.style.cssText = `
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                font-size: 14px;
                outline: none;
                box-sizing: border-box;
            `;

            fpIdInput.addEventListener('focus', () => {
                fpIdInput.style.borderColor = '#3b82f6';
                fpIdInput.style.boxShadow = '0 0 0 1px #3b82f6';
            });

            fpIdInput.addEventListener('blur', () => {
                fpIdInput.style.borderColor = '#d1d5db';
                fpIdInput.style.boxShadow = 'none';
            });

            fpIdContainer.appendChild(fpIdLabel);
            fpIdContainer.appendChild(fpIdInput);

            // Insert after company field
            companyFieldContainer.parentNode.insertBefore(fpIdContainer, companyFieldContainer.nextSibling);


        } catch (error) {
            console.error('Error injecting FP_ID field:', error);
        }
    };

    // Function to inject save button for temporary tasks
    const injectSaveButtonForTempTask = () => {
        try {
            const sidebar = document.querySelector('.wx-sidebar');
            if (!sidebar) {
                return;
            }


            // Check multiple ways if we have a temporary task
            const taskTitleElement = sidebar.querySelector('.wx-title');
            const allInputs = sidebar.querySelectorAll('input');
            let isEditingTempTask = false;

            // Method 1: Check title
            if (taskTitleElement && (taskTitleElement.textContent.includes('temp_new_task_') || taskTitleElement.textContent.includes('edit_temp_task_'))) {
                isEditingTempTask = true;
            }

            // Method 2: Check input values or IDs
            allInputs.forEach(input => {
                if (input.value && (input.value.includes('temp_new_task_') || input.value.includes('edit_temp_task_'))) {
                    isEditingTempTask = true;
                }
                if (input.id && (input.id.includes('temp_new_task_') || input.id.includes('edit_temp_task_'))) {
                    isEditingTempTask = true;
                }
            });

            // Method 3: Check if we just created a temp task recently (fallback)
            if (!isEditingTempTask) {
                isEditingTempTask = true; // Always inject for now to debug
            }


            if (isEditingTempTask && !sidebar.querySelector('.temp-task-save-button')) {
                // Create save button
                const saveButtonContainer = document.createElement('div');
                saveButtonContainer.style.cssText = `
                    padding: 16px;
                    border-top: 1px solid #e5e7eb;
                    background-color: #f9fafb;
                `;

                const saveButton = document.createElement('button');
                saveButton.className = 'temp-task-save-button';

                // Use different text based on edit mode
                const isEditingRef = editModeRef.current.isEditing;
                if (isEditMode || isEditingRef) {
                    saveButton.textContent = 'MA-Beratungslauf ändern';
                } else {
                    saveButton.textContent = 'MA-Beratungslauf erstellen';
                }
                saveButton.style.cssText = `
                    width: 100%;
                    padding: 12px 24px;
                    background-color: #3A46A9;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    font-size: 14px;
                `;

                // Add hover effect
                saveButton.addEventListener('mouseenter', () => {
                    saveButton.style.backgroundColor = '#2d3590';
                });
                saveButton.addEventListener('mouseleave', () => {
                    saveButton.style.backgroundColor = '#3A46A9';
                });

                // Add click handler
                saveButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleManualSave();
                });

                saveButtonContainer.appendChild(saveButton);

                // Insert at the bottom of the sidebar
                sidebar.appendChild(saveButtonContainer);
            }
        } catch (error) {
            console.error('Error injecting save button:', error);
        }
    };

    // Function to hook the existing save button
    const hookExistingSaveButton = () => {
        try {

            // Look for the save button in the sidebar
            const saveButtons = document.querySelectorAll('.wx-sidebar button, .wx-sidebar [role="button"]');

            saveButtons.forEach((button, index) => {
                const buttonText = button.textContent.trim();

                // Check if this is a Cancel/Close button - these should just close the sidebar
                const isCancelButton = buttonText.includes('Abbrechen') ||
                                      buttonText.includes('Cancel') ||
                                      button.className.includes('danger') ||
                                      button.className.includes('wx-danger');

                if (isCancelButton && !button.hasAttribute('data-hooked')) {
                    button.setAttribute('data-hooked', 'true');

                    button.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();

                        // Hide temporary tasks from DOM (don't delete via API to avoid state issues)
                        const tempTasks = document.querySelectorAll('[data-id^="temp_new_task_"], [data-id^="edit_temp_task_"]');
                        tempTasks.forEach(tempTask => {
                            tempTask.style.display = 'none';
                        });

                        // Reset edit mode state
                        setIsEditMode(false);
                        setEditingTaskData(null);
                        editModeRef.current = { isEditing: false, taskData: null };

                        // Reset sidebar injection flag
                        sidebarInjectedRef.current = false;

                        // Clear processed tasks tracking
                        processedTempTasksRef.current.clear();

                        // Try to close the sidebar using API (proper way)
                        try {
                            if (apiRef.current) {
                                apiRef.current.exec("close-sidebar");
                            }
                        } catch (error) {
                            // If API close fails, force close using DOM
                            const sidebar = document.querySelector('.wx-sidebar');
                            if (sidebar) {
                                sidebar.style.display = 'none';
                            }
                            const overlay = document.querySelector('.wx-overlay');
                            if (overlay) {
                                overlay.style.display = 'none';
                            }
                        }
                    }, true);

                    return; // Skip further processing for cancel buttons
                }

                // Hook buttons that might be save buttons (but not cancel buttons)
                if (!button.hasAttribute('data-hooked') && (
                    buttonText.includes('Speichern') ||
                    buttonText.includes('Save') ||
                    buttonText.includes('MA-Beratungslauf erstellen') ||
                    buttonText.includes('MA-Beratungslauf ändern') ||
                    button.type === 'submit' ||
                    button.className.includes('save') ||
                    button.className.includes('primary')
                )) {
                    button.setAttribute('data-hooked', 'true');

                    button.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();

                        // Call our manual save function
                        handleManualSave();
                    }, true); // Use capture phase
                }
            });

            // Also look for save buttons outside sidebar but related
            const allButtons = document.querySelectorAll('button[data-id*="save"], button[class*="save"], button[type="submit"]');
            allButtons.forEach((button, index) => {
                if (!button.hasAttribute('data-hooked')) {
                    const buttonText = button.textContent.trim();

                    // Skip cancel buttons
                    if (buttonText.includes('Abbrechen') || buttonText.includes('Cancel') || button.className.includes('danger')) {
                        return;
                    }

                    if (buttonText.includes('Speichern') || buttonText.includes('Save')) {
                        button.setAttribute('data-hooked', 'true');

                        button.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation();

                            // Call our manual save function
                            handleManualSave();
                        }, true);
                    }
                }
            });

        } catch (error) {
            console.error('Error hooking save button:', error);
        }
    };

    // Function to translate button labels to German
    const translateButtonLabels = () => {
        try {

            // Find all buttons in the sidebar
            const allButtons = document.querySelectorAll('.wx-sidebar button, .wx-sidebar [role="button"]');

            allButtons.forEach((button, index) => {
                const originalText = button.textContent.trim();

                // Translate common button texts
                if (originalText.toLowerCase().includes('delete') || originalText.toLowerCase().includes('löschen')) {
                    button.textContent = 'Abbrechen';
                } else if (originalText.toLowerCase().includes('save') || originalText.toLowerCase().includes('speichern')) {
                    button.textContent = 'Speichern';
                } else if (originalText.toLowerCase().includes('cancel') || originalText.toLowerCase().includes('abbrechen')) {
                    button.textContent = 'Abbrechen';
                } else if (originalText.toLowerCase().includes('ok') || originalText.toLowerCase() === 'ok') {
                    button.textContent = 'Speichern';
                } else if (isEditMode && originalText.toLowerCase().includes('neue')) {
                    // In edit mode, replace "Neue" with "Speichern"
                    button.textContent = 'Speichern';
                } else if (originalText.includes('MA-Beratungslauf ändern')) {
                    // Keep edit mode button text as is (it's already in German)
                }
            });

            // Also check for any buttons outside sidebar that might be related
            const externalButtons = document.querySelectorAll('button[data-id*="save"], button[data-id*="delete"], button[data-id*="cancel"]');
            externalButtons.forEach((button, index) => {
                const originalText = button.textContent.trim();
                const dataId = button.getAttribute('data-id');


                if (dataId && dataId.includes('save')) {
                    button.textContent = 'Speichern';
                } else if (dataId && (dataId.includes('delete') || dataId.includes('cancel'))) {
                    button.textContent = 'Abbrechen';
                }
            });

        } catch (error) {
            console.error('Error translating button labels:', error);
        }
    };

    // Function to handle manual save from custom button
    const handleManualSave = async () => {
        try {

            // Get all form values directly from DOM with comprehensive selectors
            const allInputs = document.querySelectorAll('.wx-sidebar input, .wx-sidebar textarea, .wx-sidebar select');
            const formData = {};

            allInputs.forEach((input, index) => {
                const fieldName = input.name || input.getAttribute('data-key') || input.id || `input_${index}`;
                const value = input.value;
                formData[fieldName] = value;

            });


            // Try multiple ways to get FP_ID, including from edit mode data
            let fpId = formData.FP_ID || formData.fpId || formData['FP-ID'];

            // In edit mode, use the stored FP_ID from the original task
            if (!fpId && isEditMode && editingTaskData) {
                fpId = editingTaskData.id;
            }

            // Try global variables as fallback (from company search)
            if (!fpId && window.selectedCompanyFPID) {
                fpId = window.selectedCompanyFPID;
            }

            // If not found in formData, try direct DOM queries with priority order
            if (!fpId) {
                // Prioritize actual FP_ID fields first
                const fpInputs = [
                    // Highest priority: actual FP_ID fields
                    document.querySelector('.wx-sidebar input[name="FP_ID"]'),
                    document.querySelector('.wx-sidebar input[data-key="FP_ID"]'),
                    document.querySelector('.wx-sidebar input[type="number"]'),
                    document.querySelector('.wx-sidebar input[placeholder*="FP-ID"]'),
                    // Lower priority: other inputs, but exclude date-like values
                    ...Array.from(document.querySelectorAll('.wx-sidebar input')).filter(input => {
                        // Exclude date inputs and inputs that look like dates
                        const value = input.value || '';
                        const isDateLike = /\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}/.test(value);
                        const isDateType = input.type === 'date';
                        const isCompanySearch = input.placeholder && input.placeholder.includes('Firmennamen');

                        return !isDateLike && !isDateType && !isCompanySearch;
                    })
                ].filter(Boolean);

                for (let index = 0; index < fpInputs.length; index++) {
                    const input = fpInputs[index];

                    // Check for data attribute first (from company search)
                    const dataAttributeValue = input.getAttribute('data-selected-fp-id');
                    if (dataAttributeValue) {
                        fpId = dataAttributeValue;
                        break;
                    }

                    if (input.value && input.value.trim() !== '') {
                        const value = input.value.trim();

                        console.log({
                            value: value,
                            name: input.name,
                            type: input.type,
                            placeholder: input.placeholder,
                            id: input.id,
                            priority: index < 4 ? 'HIGH' : 'LOW'
                        });

                        // Only use purely numeric values as FP_ID
                        const isNumeric = /^\d+$/.test(value);
                        const isDateLike = /\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}/.test(value);

                        if (isNumeric && !isDateLike) {
                            fpId = value;
                            break; // Stop at first valid FP_ID
                        } else if (isDateLike) {
                        } else {
                        }
                    }
                }
            }

            // Try multiple ways to get company name
            let companyName = formData.companySearch || formData.text;

            // In edit mode, use the company name from the original task data
            if (!companyName && isEditMode && editingTaskData) {
                companyName = editingTaskData.text;
            }

            if (!companyName) {
                const companyInputs = [
                    document.querySelector('.wx-sidebar input[placeholder*="Firmennamen"]'),
                    document.querySelector('.wx-sidebar input[placeholder*="eingeben"]'),
                    document.querySelector('.wx-sidebar input[type="text"]:first-of-type')
                ].filter(Boolean);

                companyInputs.forEach(input => {
                    if (input.value && input.value.trim() !== '' && !companyName) {
                        const value = input.value.trim();
                        // Don't use purely numeric values as company names
                        const isNumeric = /^\d+$/.test(value);
                        if (!isNumeric) {
                            companyName = value;
                        } else {
                        }
                    }
                });
            }

            // Get dates from the allInputs we already collected (don't query again!)
            // Filter for inputs with date-like values (MM/DD/YYYY or DD.MM.YYYY)
            const dateInputs = Array.from(allInputs).filter(input => {
                // Only look at input elements (not textarea)
                if (input.tagName !== 'INPUT') return false;

                const value = input.value;
                // Match MM/DD/YYYY or DD.MM.YYYY format
                const isDateValue = value && (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/) || value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/));

                if (isDateValue) {
                }

                return isDateValue;
            });


            const startDateInput = dateInputs[0];
            const endDateInput = dateInputs[1];

            // Helper function to parse date from input value
            const getDateValue = (input) => {
                if (!input || !input.value) return null;

                const value = input.value;

                // Check if it has stored ISO value
                const isoValue = input.getAttribute('data-iso-value');
                if (isoValue) {
                    return new Date(isoValue);
                }

                // Parse DD.MM.YYYY format
                if (value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                    const [day, month, year] = value.split('.');
                    const date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                    return date;
                }

                // Parse MM/DD/YYYY format
                if (value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    const [month, day, year] = value.split('/');
                    const date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                    return date;
                }

                return null;
            };

            let startDate = getDateValue(startDateInput);
            let endDate = getDateValue(endDateInput);

            // If we couldn't parse dates from inputs, log why and use defaults
            if (!startDate) {
                console.warn('Could not parse start date from input, using current date as fallback');
                startDate = new Date();
            }
            if (!endDate) {
                console.warn('Could not parse end date from input, using 30 days from now as fallback');
                endDate = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000));
            }

            console.log({
                start: startDate.toISOString(),
                end: endDate.toISOString()
            });

            console.log({
                fpId,
                companyName,
                startDate,
                endDate,
                planer: user.email
            });


            // Validate and clean FP_ID
            if (!fpId || fpId.toString().trim() === '') {
                toast.error('FP-ID ist erforderlich');
                return;
            }

            // Clean and validate FP_ID is a valid number
            const cleanFpId = fpId.toString().trim();
            const numericFpId = parseInt(cleanFpId, 10);

            if (isNaN(numericFpId)) {
                toast.error(`FP-ID "${cleanFpId}" ist keine gültige Zahl`);
                return;
            }


            // Create task data object with clean numeric FP_ID
            const taskData = {
                FP_ID: numericFpId,
                text: companyName || `FP-${numericFpId} - MA-Beratungslauf`,
                start: startDate,
                end: endDate,
                planer: user.email
            };

            // Find and remove the temporary task (both new and edit temp tasks)
            const tempTaskElements = document.querySelectorAll('[data-id*="temp_new_task_"], [data-id*="edit_temp_task_"]');
            let tempTaskId = null;

            tempTaskElements.forEach(el => {
                const taskId = el.getAttribute('data-id');
                if (taskId && (taskId.includes('temp_new_task_') || taskId.includes('edit_temp_task_'))) {
                    tempTaskId = taskId;
                }
            });

            if (tempTaskId) {
                try {
                    apiRef.current.exec("delete-task", { id: tempTaskId });
                } catch (deleteError) {
                }
            }

            // Call the appropriate function based on mode (create or edit) - check both state and ref
            const isEditingRef = editModeRef.current.isEditing;
            const editDataRef = editModeRef.current.taskData;

            let success = false;
            if ((isEditMode && editingTaskData) || (isEditingRef && editDataRef)) {
                success = await handleEditConsCompanyUpdate(taskData, numericFpId);
            } else {
                success = await handleNewConsCompanyCreation(taskData, numericFpId);
            }

            // Only close sidebar and reset state if operation was successful
            if (success) {
                // Reset edit mode after successful operation (reset both state and ref)
                if (isEditMode || editModeRef.current.isEditing) {
                    setIsEditMode(false);
                    setEditingTaskData(null);
                    editModeRef.current = { isEditing: false, taskData: null };
                }

                // Close the sidebar after successful operation
                try {
                    apiRef.current.exec("close-sidebar");
                } catch (e) {
                    // Sidebar close failed
                }
            }

        } catch (error) {
            console.error('Error in manual save:', error);
            // Only show generic error if not already handled by inner function
            // Inner functions will re-throw errors they don't handle specifically
            if (error) {
                toast.error('Fehler beim Speichern');
            }
        }
    };

    // Function to hide temporary task row from view
    const hideTemporaryTaskRow = (taskId) => {
        try {

            // Find task elements by various possible selectors
            const possibleSelectors = [
                `[data-id="${taskId}"]`,
                `[data-task-id="${taskId}"]`,
                `[id*="${taskId}"]`,
                `[class*="${taskId}"]`
            ];

            let taskElement = null;
            for (const selector of possibleSelectors) {
                taskElement = document.querySelector(selector);
                if (taskElement) {
                    break;
                }
            }

            if (taskElement) {
                // Hide the task row with CSS
                taskElement.style.display = 'none';
                taskElement.style.visibility = 'hidden';
                taskElement.style.height = '0px';
                taskElement.style.overflow = 'hidden';

                // Also hide parent row if it's a table row structure
                const parentRow = taskElement.closest('tr, .wx-row, .gantt-row, [class*="row"]');
                if (parentRow && parentRow !== taskElement) {
                    parentRow.style.display = 'none';
                    parentRow.style.visibility = 'hidden';
                    parentRow.style.height = '0px';
                }

            } else {

                // Try again with a delay
                setTimeout(() => {
                    hideTemporaryTaskRow(taskId);
                }, 200);
            }

        } catch (error) {
            console.error('Error hiding task row:', error);
        }
    };

    // Function to open consultant edit modal with pre-populated data
    const openConsultantEditModal = (consultantTask) => {
        try {
            console.log('Opening consultant edit modal for:', consultantTask);

            // Extract parent FP ID from consultant task ID (format: "fpId_consultant_partnernr")
            const parentFpId = consultantTask.parent || consultantTask.id.toString().split('_consultant_')[0];

            // Get dates from the consultant task
            const consultantStart = consultantTask.start ? new Date(consultantTask.start) : new Date();
            const consultantEnd = consultantTask.end ? new Date(consultantTask.end) : new Date();

            // Don't clear UI when opening from consultant click
            // Open modal with consultant's current data pre-populated
            setModalData({
                taskId: parentFpId,
                fromDate: consultantStart,
                toDate: consultantEnd,
                consultantName: consultantTask.text, // Pre-populate consultant name
                editMode: true, // Flag to indicate we're editing
                existingConsultantId: consultantTask.id // Keep track of which consultant we're editing
            });
            setConsultantModalOpen(true);

        } catch (error) {
            console.error('Error in openConsultantEditModal:', error);
            toast.error('Fehler beim Öffnen des Bearbeitungsdialogs');
        }
    };

    // Function to show add consultant dialog
    const showAddConsultantDialog = (taskId, shouldClearUI = true) => {
        try {

            // Get parent task to calculate proper dates
            const parentTask = tasks.find(task => task.id === taskId);
            const parentStart = parentTask ? new Date(parentTask.start) : new Date();
            const parentEnd = parentTask ? new Date(parentTask.end) : new Date(Date.now() + (14 * 24 * 60 * 60 * 1000));

            // Only clear message and reload tracking if explicitly requested
            // (e.g., when clicking from "Keine Berater" message, NOT from "Weitere Berater" button)
            if (shouldClearUI) {
                clearMessageForFP(taskId);
                loadedTasksRef.current.delete(taskId);
            }

            // Open modal with task data
            setModalData({
                taskId: taskId,
                fromDate: parentStart,
                toDate: parentEnd
            });
            setConsultantModalOpen(true);

        } catch (error) {
            console.error('Error in showAddConsultantDialog:', error);
            toast.error('Fehler beim Öffnen des Dialogs');
        }
    };

    // Function to handle consultant selection from modal
    const handleConsultantSelected = (consultant, fromDate, toDate) => {
        try {

            if (apiRef.current) {
                // Remove all existing consultant subtasks for this FP
                const consultantTasksToRemove = tasks.filter(task =>
                    task.parent === modalData.taskId && task.type === "consultant"
                );


                consultantTasksToRemove.forEach(task => {
                    try {
                        apiRef.current.exec("delete-task", { id: task.id });
                    } catch (err) {
                        console.warn(`Could not remove task ${task.id}:`, err);
                    }
                });

                // Clear any "no consultants" message task
                clearNoConsultantsTask(modalData.taskId);

                // Clear the caching flags so the API call will execute
                loadedTasksRef.current.delete(modalData.taskId);
                pendingRequestsRef.current.delete(modalData.taskId);


                // Reload consultants from API to get fresh data including the newly added consultant
                getFPConsultants(modalData.taskId);

                toast.success(`Berater "${consultant.fullname || consultant.name}" wurde erfolgreich hinzugefügt`);
            }
        } catch (error) {
            console.error('Error reloading consultants:', error);
            toast.error('Fehler beim Aktualisieren der Berater-Ansicht');
        }
    };

    // Function to pre-populate form fields in edit mode
    const prePopulateEditFields = () => {
        // Check both state and ref for edit mode data
        const editData = editingTaskData || editModeRef.current.taskData;
        const isEditing = isEditMode || editModeRef.current.isEditing;

        if (!isEditing || !editData) {
            console.log({
                isEditMode,
                hasEditingTaskData: !!editingTaskData,
                isEditingRef: editModeRef.current.isEditing,
                hasEditDataRef: !!editModeRef.current.taskData
            });
            return;
        }


        // Format date inputs FIRST before pre-populating
        formatDateInputs();

        // Wait a bit for formatting to complete, then continue with pre-population
        setTimeout(() => {
        try {
            // In edit mode, replace company search field with plain text display
            const companySearchField = document.querySelector('.wx-sidebar input[placeholder*="Firmennamen"]') ||
                                      document.querySelector('.wx-sidebar input[data-key="companySearch"]') ||
                                      document.querySelector('.wx-sidebar input[name="companySearch"]') ||
                                      document.querySelector('.wx-sidebar input[type="text"]:first-of-type');

            if (companySearchField) {
                // Replace input field with text display
                const parentDiv = companySearchField.closest('.wx-field');
                if (parentDiv) {
                    // Create a text display element
                    const textDisplay = document.createElement('div');
                    textDisplay.style.cssText = `
                        padding: 8px 12px;
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                        border-radius: 4px;
                        font-weight: 500;
                        color: #495057;
                        margin-bottom: 8px;
                    `;
                    textDisplay.innerHTML = `<strong>Firmenprojekt:</strong> ${editData.text || 'Unbekannt'}`;

                    // Replace the entire field
                    parentDiv.innerHTML = '';
                    parentDiv.appendChild(textDisplay);

                }
            } else {
            }

            // Remove FP_ID field completely (user doesn't need to see it)
            const fpIdField = document.querySelector('.wx-sidebar input[type="number"]') ||
                             document.querySelector('.wx-sidebar input[name="FP_ID"]') ||
                             document.querySelector('.wx-sidebar input[data-key="FP_ID"]');

            if (fpIdField) {
                const fpIdParent = fpIdField.closest('.wx-field');
                if (fpIdParent) {
                    fpIdParent.style.display = 'none';

                    // But keep the value accessible for form submission
                    fpIdField.value = editData.id || '';
                    fpIdField.setAttribute('data-edit-fp-id', editData.id || '');
                }
            } else {
            }

            // Pre-populate date fields - wx-react-gantt uses text inputs
            const dateTextInputs2 = document.querySelectorAll('.wx-sidebar input[type="text"]');
            const dateInputs = Array.from(dateTextInputs2).filter(input => {
                const value = input.value;
                // Empty inputs or inputs with date-like values
                return !value || value.match(/^\d{1,2}[\/\.]\d{1,2}\/\d{4}$/);
            }).slice(0, 2); // Take first 2 date inputs


            if (dateInputs.length >= 2 && editData.start && editData.end) {
                const startDate = new Date(editData.start);
                const endDate = new Date(editData.end);

                // Format as DD.MM.YYYY
                const startFormatted = `${String(startDate.getDate()).padStart(2, '0')}.${String(startDate.getMonth() + 1).padStart(2, '0')}.${startDate.getFullYear()}`;
                const endFormatted = `${String(endDate.getDate()).padStart(2, '0')}.${String(endDate.getMonth() + 1).padStart(2, '0')}.${endDate.getFullYear()}`;

                if (dateInputs[0]) {
                    dateInputs[0].value = startFormatted;
                    dateInputs[0].setAttribute('data-date-formatted', 'true');
                    dateInputs[0].setAttribute('data-iso-value', startDate.toISOString().split('T')[0]);
                    dateInputs[0].placeholder = 'TT.MM.JJJJ';
                    dateInputs[0].dispatchEvent(new Event('input', { bubbles: true }));
                }

                if (dateInputs[1]) {
                    dateInputs[1].value = endFormatted;
                    dateInputs[1].setAttribute('data-date-formatted', 'true');
                    dateInputs[1].setAttribute('data-iso-value', endDate.toISOString().split('T')[0]);
                    dateInputs[1].placeholder = 'TT.MM.JJJJ';
                    dateInputs[1].dispatchEvent(new Event('input', { bubbles: true }));
                }

            } else {
            }

            // Pre-populate details field
            const detailsField = document.querySelector('.wx-sidebar textarea') ||
                                document.querySelector('.wx-sidebar input[data-key="details"]');

            if (detailsField && editData.details) {
                detailsField.value = editData.details;
                detailsField.dispatchEvent(new Event('input', { bubbles: true }));
            }


        } catch (error) {
            console.error('Error pre-populating fields:', error);
        }

        }, 150); // End of setTimeout for date formatting
    };

    // Function to add single and double-click event handlers for task editing
    const addDoubleClickHandler = () => {
        // Prevent multiple attachments
        if (clickHandlersAttachedRef.current) {
            return;
        }

        try {
            // Wait for the Gantt chart to be fully rendered
            setTimeout(() => {
                const ganttElement = document.querySelector('.wx-gantt');
                if (!ganttElement) return;

                // Check if handlers are already attached via DOM attribute
                if (ganttElement.hasAttribute('data-click-handlers-attached')) {
                    return;
                }
                ganttElement.setAttribute('data-click-handlers-attached', 'true');
                clickHandlersAttachedRef.current = true;

                let clickTimer = null;
                let clickCount = 0;

                // Add click event listener to the gantt grid area (LEFT SIDE) with event capture
                ganttElement.addEventListener('click', (event) => {
                    // Ignore clicks on toggle icons, buttons, and message rows
                    if (event.target.closest('.wx-toggle-icon') ||
                        event.target.closest('button') ||
                        event.target.closest('[data-message-for]')) {
                        return;
                    }

                    // Find the closest task row element
                    const taskRow = event.target.closest('[data-id]');
                    if (!taskRow) return;

                    const taskId = taskRow.getAttribute('data-id');
                    if (!taskId) return;

                    // Find the task data from the tasks array
                    const taskData = tasks.find(task => task.id.toString() === taskId.toString());

                    // Handle consultant tasks differently - open modal with pre-populated data
                    if (taskData && taskData.type === 'consultant') {
                        openConsultantEditModal(taskData);
                        return;
                    }

                    // Ignore clicks on add-button tasks - they have their own click handler
                    if (taskData && taskData.type === 'add-button') {
                        return;
                    }

                    // Only allow editing of main FP tasks, not consultant subtasks
                    if (!taskData) {
                        return;
                    }

                    // Check if click was on the Firma name cell (first column)
                    const clickedCell = event.target.closest('.wx-cell');
                    const allCells = taskRow.querySelectorAll('.wx-cell');
                    const isFirstColumn = clickedCell && allCells[0] === clickedCell;

                    // Handle single/double click on Firma name
                    if (isFirstColumn) {
                        clickCount++;

                        if (clickCount === 1) {
                            // Wait to see if it's a double-click
                            clickTimer = setTimeout(() => {
                                // Single click confirmed - open edit dialog
                                openEditDialog(taskData);
                                clickCount = 0;
                            }, 250); // 250ms delay to detect double-click
                        } else if (clickCount === 2) {
                            // Double-click detected - clear timer and open immediately
                            clearTimeout(clickTimer);
                            event.preventDefault();
                            event.stopPropagation();
                            openEditDialog(taskData);
                            clickCount = 0;
                        }
                    }
                }, true); // Use capture phase to intercept before library handlers

                // Add click handler for GANTT BARS (RIGHT SIDE - the actual blue/green bars)
                // Try multiple possible containers
                const ganttChartArea = document.querySelector('.wx-chart') ||
                                      document.querySelector('.wx-area') ||
                                      document.querySelector('.wx-timeline') ||
                                      ganttElement;

                if (ganttChartArea) {
                    const handleGanttBarClick = (event) => {
                        // Check if we clicked on a gantt bar element
                        // Try multiple possible selectors for gantt bars
                        const ganttBar = event.target.closest('.wx-bar') ||
                                        event.target.closest('[data-task-id]') ||
                                        event.target.closest('.wx-task') ||
                                        (event.target.classList && event.target.classList.contains('wx-bar') ? event.target : null);

                        if (!ganttBar) {
                            // Also check if the target itself has task-id data
                            const targetTaskId = event.target.getAttribute('data-task-id') ||
                                                event.target.getAttribute('data-id');
                            if (!targetTaskId) return;
                        }

                        // Get task ID from the bar element
                        const taskId = ganttBar ? (
                            ganttBar.getAttribute('data-task-id') ||
                            ganttBar.getAttribute('data-id') ||
                            ganttBar.getAttribute('task-id')
                        ) : (
                            event.target.getAttribute('data-task-id') ||
                            event.target.getAttribute('data-id')
                        );

                        if (!taskId) {
                            return;
                        }

                        // Get task data from Gantt API instead of React tasks array
                        // Because consultant tasks are added via apiRef.exec() and may not be in React state
                        let taskData = null;

                        try {
                            // Try to get task from Gantt API
                            if (apiRef.current && apiRef.current.getTask) {
                                taskData = apiRef.current.getTask(taskId);
                            }
                        } catch (e) {
                            // Fallback to React tasks array
                        }

                        // Fallback to React tasks array if API method doesn't work
                        if (!taskData) {
                            taskData = tasks.find(task => task.id.toString() === taskId.toString());
                        }

                        if (!taskData) {
                            // Last resort: check if taskId indicates it's a consultant and extract info
                            if (taskId.includes('_consultant_')) {
                                const parts = taskId.split('_consultant_');
                                const parentId = parts[0];
                                const partnernr = parts[1];

                                // Find consultant name from DOM
                                const consultantRow = document.querySelector(`[data-id="${taskId}"]`);
                                const consultantName = consultantRow ? consultantRow.textContent.trim().split('\n')[0] : 'Berater';

                                // Create minimal task data for modal
                                taskData = {
                                    id: taskId,
                                    text: consultantName,
                                    type: 'consultant',
                                    parent: parentId,
                                    start: new Date(),
                                    end: new Date()
                                };
                            } else {
                                return;
                            }
                        }

                        // If it's a consultant task, open the edit modal
                        if (taskData.type === 'consultant' || taskId.includes('_consultant_')) {
                            event.preventDefault();
                            event.stopPropagation();
                            openConsultantEditModal(taskData);
                        }
                    };

                    ganttChartArea.addEventListener('click', handleGanttBarClick, true);

                    // Also try adding to the entire gantt element as fallback
                    ganttElement.addEventListener('click', handleGanttBarClick, true);
                }

                // Also keep the original double-click handler for backward compatibility
                ganttElement.addEventListener('dblclick', (event) => {
                    // Ignore clicks on toggle icons, buttons, and message rows
                    if (event.target.closest('.wx-toggle-icon') ||
                        event.target.closest('button') ||
                        event.target.closest('[data-message-for]')) {
                        return;
                    }

                    // Prevent default double-click behavior
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();

                    // Find the closest task row element
                    const taskRow = event.target.closest('[data-id]');
                    if (taskRow) {
                        const taskId = taskRow.getAttribute('data-id');

                        if (taskId) {
                            // Find the task data from the tasks array
                            const taskData = tasks.find(task => task.id.toString() === taskId.toString());

                            if (taskData && taskData.type === 'consultant') {
                                // Open consultant modal with pre-populated data
                                console.log('Double-click on consultant:', taskData.text);
                                openConsultantEditModal(taskData);
                            } else if (taskData && taskData.type === 'add-button') {
                                // Ignore add-button tasks - they have their own click handler
                                return;
                            } else if (taskData) {
                                // Only allow editing of main FP tasks, not consultant subtasks
                                console.log('Double-click handler (fallback):', taskData.text);
                                openEditDialog(taskData);
                            }
                        }
                    }
                }, true); // Use capture phase to intercept before library handlers
            }, 1500); // Wait for full render
        } catch (error) {
            console.error('Error adding click handlers:', error);
        }
    };

    // Function to open edit dialog with existing task data
    const openEditDialog = (taskData) => {
        if (!taskData || !apiRef.current) {
            console.error('openEditDialog: Missing taskData or apiRef');
            return;
        }

        // Set transition flag to prevent race conditions
        sidebarTransitionRef.current = true;

        try {
            // First, hide any existing temporary tasks from DOM (don't delete via API to avoid state issues)
            const existingTempTasks = document.querySelectorAll('[data-id^="temp_new_task_"], [data-id^="edit_temp_task_"]');
            existingTempTasks.forEach(tempTask => {
                tempTask.style.display = 'none';
            });

            // Reset sidebar injection flag to allow fresh injection
            sidebarInjectedRef.current = false;

            // Clear processed temp tasks tracking
            processedTempTasksRef.current.clear();

            // Clear any previous edit state
            setIsEditMode(false);
            setEditingTaskData(null);
            editModeRef.current = { isEditing: false, taskData: null };

            // Close any existing sidebar using API
            try {
                apiRef.current.exec("close-sidebar");
            } catch (e) {
                // Ignore close errors
            }

            // Longer delay to ensure all cleanup is complete
            setTimeout(() => {
                // Clear transition flag
                sidebarTransitionRef.current = false;

                if (!apiRef.current) return;

                // Set edit mode and task data FIRST (both state and ref for immediate access)
                setIsEditMode(true);
                setEditingTaskData(taskData);
                editModeRef.current = { isEditing: true, taskData: taskData };

                // Create a temporary task that represents the edit state
                const editTempTaskId = `edit_temp_task_${Date.now()}`;
                const editTempTask = {
                    id: editTempTaskId,
                    text: taskData.text || '',
                    start: taskData.start,
                    end: taskData.end,
                    duration: taskData.duration || 1,
                    progress: taskData.progress || 0,
                    type: "task",
                    FP_ID: taskData.id, // Store original task ID as FP_ID
                    companyName: taskData.text,
                    planer: user.email,
                    details: taskData.details || '',
                    hidden: true // Hide this temporary task visually
                };

                // Add the temporary task first
                try {
                    apiRef.current.exec("add-task", {
                        task: editTempTask
                    });
                } catch (addError) {
                    console.error('Error adding edit temp task:', addError);
                    sidebarTransitionRef.current = false;
                    return;
                }

                // Then show sidebar with the temporary task
                setTimeout(() => {
                    if (!apiRef.current) return;

                    try {
                        apiRef.current.exec("show-sidebar", {
                            task: editTempTask
                        });

                        // Explicitly format date inputs after showing sidebar
                        setTimeout(() => formatDateInputs(), 100);
                        setTimeout(() => formatDateInputs(), 200);
                        setTimeout(() => formatDateInputs(), 300);
                        setTimeout(() => formatDateInputs(), 500);
                    } catch (showError) {
                        console.error('Error showing sidebar:', showError);
                    }
                }, 200);
            }, 300);

        } catch (error) {
            console.error('Error opening edit dialog:', error);
            sidebarTransitionRef.current = false;
            toast.error('Fehler beim Öffnen des Bearbeitungsdialogs');
        }
    };

    return (
        <Willow>
            <div onContextMenu={(ev) => {
                ev.preventDefault();
                if (menuHandler.current) {
                    menuHandler.current(ev);
                }
            }} className={`planung-kalender-wrapper bg-white ${currentViewMode === 'workweek' ? 'workweek-mode' : ''} ${currentViewMode === 'month' ? 'month-mode' : ''}`} style={{height}}>
                <div className="flex gap-2">

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSettingsOpen(!settingsOpen);
                        }}
                        className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded transition"
                    >
                        {settingsOpen ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                        Einstellungen anzeigen
                    </button>

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            scrollToTodayLeftEdge();
                        }}
                        className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 rounded transition"
                        title="Scroll to today's date"
                    >
                        Heute anzeigen
                    </button>
                </div>

                <div
                    className={`transition-all duration-300 overflow-hidden`}
                    style={{ maxHeight: settingsOpen ? '400px' : '0px' }}
                >
                    <div className="p-2">
                        <label className="mr-2 font-semibold">Zeiteinheit:</label>
                        <select
                            value={lengthUnit}
                            onChange={(e) => setLengthUnit(e.target.value)}
                            className="border p-1 rounded"
                        >
                            <option value="hour">Stunde</option>
                            <option value="day">Tag</option>
                            <option value="week">Woche</option>
                            <option value="month">Monat</option>
                            <option value="quarter">Quartal</option>
                        </select>
                    </div>
                    <div className="p-2">
                        <Slider label="Cell width" value={cellWidth} min={20} max={200} onChange={(ev) => setCellWidth(ev.value)} />
                        <Slider label="Cell height" value={cellHeight} min={20} max={60} onChange={(ev) => setCellHeight(ev.value)} />
                        <Slider label="Scale height" value={scaleHeight} min={20} max={60} onChange={(ev) => setScaleHeight(ev.value)} />
                    </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                    <Toolbar api={apiRef.current} items={translatedButtons} />

                    {/* View Mode Buttons */}
                    <div className="flex items-center gap-1 ml-4">
                        <button
                            onClick={() => handleViewModeChange('workweek')}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded transition ${
                                currentViewMode === 'workweek'
                                ? 'view-mode-active'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            Arbeitswoche
                        </button>

                        <button
                            onClick={() => handleViewModeChange('week')}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded transition ${
                                currentViewMode === 'week'
                                ? 'view-mode-active'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            Woche
                        </button>

                        <button
                            onClick={() => handleViewModeChange('month')}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded transition ${
                                currentViewMode === 'month'
                                ? 'view-mode-active'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            Monat
                        </button>

                        {/* Navigation arrows */}
                        <button
                            onClick={() => handleNavigate('prev')}
                            className="flex items-center justify-center w-8 h-8 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition ml-2"
                            title="Vorherige Periode"
                        >
                            <FaChevronLeft size={12} />
                        </button>

                        <button
                            onClick={() => handleNavigate('next')}
                            className="flex items-center justify-center w-8 h-8 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition"
                            title="Nächste Periode"
                        >
                            <FaChevronRight size={12} />
                        </button>

                        {/* Current period display */}
                        <div className="flex items-center px-3 py-2 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded ml-2">
                            {viewPeriodText || 'Lade...'}
                        </div>

                        <button
                            onClick={handleTodayClick}
                            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded transition ml-2 ${
                                todayButtonActive ? 'today-button-active' : 'today-button-inactive'
                            }`}
                            title="Zur aktuellen Periode springen"
                        >
                            Heute
                        </button>
                    </div>
                </div>
                <Gantt
                    api={apiRef}
                    tasks={tasks}
                    links={links}
                    scales={currentScales}
                    taskTypes={taskTypes}
                    editorShape={editorShape}
                    highlightTime={highlightTime}
                    columns={columns}
                    columnShape={{ action: false }}
                    taskTemplate={TaskTemplateComponent}
                    cellWidth={cellWidth}
                    cellHeight={cellHeight}
                    scaleHeight={scaleHeight}
                    sidebar={true}
                    editor={true}
                    locale={germanLocale}
                    start={ganttStart || start}
                    end={ganttEnd || end}
                    // zoom={zoomConfig}
                />
                <ContextMenu api={apiRef.current} options={translatedContextMenu} init={v => menuHandler.current = v}></ContextMenu>
            </div>

            {/* Consultant Selection Modal */}
            <ConsultantSelectionModal
                isOpen={consultantModalOpen}
                onClose={() => setConsultantModalOpen(false)}
                onConsultantSelected={handleConsultantSelected}
                fromDate={modalData.fromDate}
                toDate={modalData.toDate}
                taskId={modalData.taskId}
                portal={portal}
                consultantName={modalData.consultantName}
                editMode={modalData.editMode}
                existingConsultantId={modalData.existingConsultantId}
            />

        </Willow>
    );
};

export default CustomGantt;
